/**
 * DTO condivisi — copia per il progetto `frontend/`.
 *
 * IMPORTANTE: questo file è DUPLICATO MANUALMENTE tra `api/src/shared/dto.ts` e
 * `frontend/src/shared/dto.ts` (nessun workspace, nessun pacchetto condiviso —
 * Req. 11.3). I due file DEVONO essere mantenuti allineati byte per byte:
 * ogni modifica qui va replicata nell'altra copia.
 *
 * Le forme dei tipi derivano dalla sezione "Design delle API" e "DTO condivisi"
 * del design (design.md).
 */

// ---------------------------------------------------------------------------
// Ruoli e viste
// ---------------------------------------------------------------------------

/** Ruolo applicativo dello User. */
export type Role = 'admin' | 'manager' | 'reviewer' | 'employer';

// ---------------------------------------------------------------------------
// Scala Livello (condivisa API/Frontend)
// ---------------------------------------------------------------------------

/**
 * Valori ammessi della scala Livello, in ordine crescente. Il primo valore
 * `e-0` ("Nessuno") è il Livello minimo: rappresenta una skill valutata ma
 * priva di competenza, che produce uno Score_Skill pari a `0` (distinto
 * dall'assenza di valutazione, rappresentata da `null`).
 */
export const LIVELLO_VALUES = ['e-0', 'e-1', 'e-2', 'e-3', 'e-4', 'e-5'] as const;

/**
 * Sottoinsieme dei Livelli "significativi" `e-1..e-5`, escluso il Livello
 * minimo `e-0` ("Nessuno"). Usato dove `e-0` non ha senso come valore
 * generato/segnaposto (es. domande del quiz AI, che misurano una competenza
 * effettiva). NON usare `LIVELLO_VALUES[0]` per questi scopi: da quando `e-0`
 * è in testa alla scala, il primo elemento è proprio `e-0`.
 */
export const LIVELLO_VALUES_EVALUABLE = ['e-1', 'e-2', 'e-3', 'e-4', 'e-5'] as const;

/** Tipo del Livello: uno dei valori della scala. */
export type Livello = (typeof LIVELLO_VALUES)[number];

/** Etichetta descrittiva per ciascun Livello. */
export const LIVELLO_LABELS: Record<Livello, string> = {
  'e-0': 'Nessuno',
  'e-1': 'Esecuzione',
  'e-2': 'Applicazione',
  'e-3': 'Gestione',
  'e-4': 'Guida',
  'e-5': 'Visione',
};

/**
 * Descrizione estesa di ciascun Livello, usata nella guida contestuale (modale)
 * accanto al selettore. Testo generico, valido per qualsiasi competenza.
 */
export const LIVELLO_DESCRIPTIONS: Record<Livello, string> = {
  'e-0':
    'La risorsa non possiede la competenza. Il punteggio della skill è azzerato.',
  'e-1':
    'La risorsa segue procedure definite da altri. Non prende decisioni strategiche.',
  'e-2':
    'La risorsa applica le regole in contesti noti, risolvendo problemi standard.',
  'e-3':
    'La risorsa gestisce autonomamente i processi, adattando le procedure a situazioni specifiche e coordinando altri.',
  'e-4':
    "La risorsa definisce le politiche, valuta l'impatto strategico e guida l'organizzazione verso nuovi standard.",
  'e-5':
    "La risorsa influenza l'intero settore o l'organizzazione a livello di board, definendo la visione strategica a lungo termine.",
};

/** Type guard: `true` se `v` è un Livello valido. */
export function isLivello(v: unknown): v is Livello {
  return typeof v === 'string' && (LIVELLO_VALUES as readonly string[]).includes(v);
}

/**
 * Rango ordinale del Livello (posizione 0-based nella scala): `e-0 → 0`,
 * `e-1 → 1`, … `e-5 → 5`. Il Livello minimo `e-0` ("Nessuno") ha rango `0`, così
 * che alimenti uno Score_Skill nullo; i Livelli `e-1..e-5` conservano i ranghi
 * storici `1..5` (nessuna migrazione dei dati esistenti).
 */
export function livelloRank(l: Livello): number {
  return LIVELLO_VALUES.indexOf(l);
}

// ---------------------------------------------------------------------------
// Fascia di anni di esperienza (condivisa API/Frontend)
// ---------------------------------------------------------------------------

/**
 * Fasce di anni di esperienza dichiarabili accanto all'autovalutazione, in
 * ordine crescente. Dato puramente informativo: non concorre ad alcun calcolo.
 */
export const EXPERIENCE_RANGE_VALUES = [
  'lt-1',
  '1-3',
  '3-5',
  '5-10',
  'gt-10',
] as const;

/** Fascia di anni di esperienza: uno dei valori della scala. */
export type ExperienceRange = (typeof EXPERIENCE_RANGE_VALUES)[number];

/** Etichetta descrittiva per ciascuna fascia di anni di esperienza. */
export const EXPERIENCE_RANGE_LABELS: Record<ExperienceRange, string> = {
  'lt-1': 'Meno di 1 anno',
  '1-3': '1-3 anni',
  '3-5': '3-5 anni',
  '5-10': '5-10 anni',
  'gt-10': 'Oltre 10 anni',
};

/** Type guard: `true` se `v` è una fascia di anni di esperienza valida. */
export function isExperienceRange(v: unknown): v is ExperienceRange {
  return (
    typeof v === 'string' &&
    (EXPERIENCE_RANGE_VALUES as readonly string[]).includes(v)
  );
}

// ---------------------------------------------------------------------------
// Tipo di skill (condiviso API/Frontend)
// ---------------------------------------------------------------------------

/**
 * Tipi ammessi per una skill, secondo la classificazione e-CF 3.0:
 * - `knowledge`: una conoscenza ("Conoscere...");
 * - `skill`: un'abilità ("Saper...");
 * - `technology`: una tecnologia o uno strumento concreto (es. Docker, SEO).
 */
export const SKILL_TYPE_VALUES = ['knowledge', 'skill', 'technology'] as const;

/** Tipo di una skill: uno dei valori ammessi. */
export type SkillType = (typeof SKILL_TYPE_VALUES)[number];

/** Etichetta descrittiva per ciascun tipo di skill. */
export const SKILL_TYPE_LABELS: Record<SkillType, string> = {
  knowledge: 'Conoscenza',
  skill: 'Abilità',
  technology: 'Tecnologia',
};

/** Type guard: `true` se `v` è un tipo di skill valido. */
export function isSkillType(v: unknown): v is SkillType {
  return typeof v === 'string' && (SKILL_TYPE_VALUES as readonly string[]).includes(v);
}

/**
 * Vista dei voti di una skill per un utente: volontà di approfondire (Self a
 * stelle informativo), Livelli aggregati delle fonti (reviewer/manager/AI) e
 * Score_Skill derivato.
 *
 * I valori assenti sono `null` (distinto dal minimo/`0`). Un rango `null`
 * indica che nessuna fonte di quel tipo ha ancora votato; i `*Count` riportano
 * quanti valutatori distinti concorrono al rango aggregato.
 */
export interface AssessmentView {
  skillId: string;
  skillName: string;
  /**
   * Voto a stelle "volontà di approfondire" (`0..10`), dato puramente
   * informativo, oppure `null` se nessuna preferenza è stata espressa.
   */
  selfInterest: number | null;
  /** Livello stimato dal quiz AI, oppure `null` se nessuna valutazione AI. */
  aiLevel: Livello | null;
  /**
   * Rango di Livello aggregato dei reviewer (media dei Rango_Livello, `[1,5]`
   * continuo) oppure `null` se nessun reviewer ha votato.
   */
  reviewerLevelRank: number | null;
  /** Numero di reviewer distinti che hanno votato la skill. */
  reviewerCount: number;
  /**
   * Livello concordato dai reviewer: valorizzato **solo** se tutti i reviewer
   * hanno votato lo **stesso** Livello. `null` se nessun reviewer ha votato o
   * se i voti divergono (livelli multipli).
   */
  reviewerUniformLevel: Livello | null;
  /**
   * Rango di Livello aggregato dei manager (media dei Rango_Livello, `[1,5]`
   * continuo) oppure `null` se nessun manager ha votato.
   */
  managerLevelRank: number | null;
  /** Numero di manager distinti che hanno votato la skill. */
  managerCount: number;
  /**
   * Livello concordato dai manager: valorizzato **solo** se tutti i manager
   * hanno votato lo **stesso** Livello. `null` se nessun manager ha votato o
   * se i voti divergono.
   */
  managerUniformLevel: Livello | null;
  /** Livello dichiarato in autovalutazione, oppure `null` se assente. */
  livelloSelf: Livello | null;
  /**
   * Fascia di anni di esperienza dichiarata in autovalutazione, oppure `null`
   * se non dichiarata. Dato puramente informativo.
   */
  yearsOfExperience: ExperienceRange | null;
  /**
   * Score_Skill derivato dalla coppia (Livello_Aggregato, Esperienza), oppure
   * `null` se il Livello_Aggregato è assente. Normalmente in `[minScore, 10]`,
   * ma **può superare 10** quando l'utente possiede certificazioni valide
   * collegate alla skill e `certificationBonus > 0`: in tal caso la UI lo
   * mostra come "10+" (in oro). Non c'è un limite superiore fisso.
   */
  score: number | null;
  /**
   * Numero di certificazioni **valide** (non scadute) possedute dall'utente e
   * collegate a questa skill. `0` se nessuna. È il conteggio che alimenta il
   * bonus certificazioni incorporato in `score`; la UI lo mostra come badge
   * (icona + numero) accanto allo Score e lo usa per spiegarne il calcolo.
   */
  certificationCount: number;
  /**
   * Singoli voti di Livello espressi dai valutatori (reviewer/manager) su questa
   * skill, **uno per valutatore** (l'ultima submission per valutatore). Sono
   * **anonimi** (nessuna identità del valutatore, solo il ruolo) e servono alla
   * UI per elencare le revisioni una per una. Array vuoto se nessuno ha votato.
   * Questi voti sono i medesimi che alimentano i ranghi aggregati
   * (`reviewerLevelRank`/`managerLevelRank`).
   */
  reviews: SkillReviewVote[];
}

/**
 * Singolo voto di Livello di un valutatore su una skill, in forma **anonima**:
 * espone solo il ruolo del valutatore e il Livello votato, senza identità.
 */
export interface SkillReviewVote {
  /** Ruolo del valutatore che ha espresso il voto. */
  raterRole: 'reviewer' | 'manager';
  /** Livello votato dal valutatore per la skill. */
  livello: Livello;
}

// ---------------------------------------------------------------------------
// Entità esposte dalle API
// ---------------------------------------------------------------------------

/** Rappresentazione di uno User esposta dalle API (`_id` serializzato come stringa). */
export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  departmentId: string | null;
  /**
   * Team di appartenenza dell'utente (dentro la propria Business Unit), oppure
   * `null` se non assegnato ad alcun team. Il team, se presente, appartiene
   * sempre alla Business Unit `departmentId`.
   */
  teamId: string | null;
  /** Abilitazione della valutazione AI per questo utente (default `false`). */
  aiEnabled: boolean;
  /** Abilitazione dell'accesso alla sezione "Livelli" per questo utente (default `false`). */
  levelsEnabled: boolean;
  /** Abilitazione dell'accesso alla sezione "CV" per questo utente (default `false`). */
  cvEnabled: boolean;
  /** URL del profilo Credly collegato dall'utente, oppure `null` se non collegato. */
  credlyProfileUrl: string | null;
  /**
   * `true` se l'utente ha collegato il proprio account LinkedIn (consenso DMA
   * attivo). Il token sensibile vive nella collezione dedicata; qui si espone
   * solo il flag per permettere alla UI di mostrare "Importa" invece di
   * "Collega". Default `false`.
   */
  linkedinLinked: boolean;
}

/**
 * User valutabile nella pagina di Review: la `UserDto` di base arricchita con
 * lo stato di review utile alla lista.
 *
 * - `myLastReviewAt`: data ISO dell'ultima valutazione inviata **dal
 *   richiedente** verso questo utente, oppure `null` se non lo ha mai valutato.
 * - `reviewedByOthers`: `true` se **almeno un altro** valutatore (diverso dal
 *   richiedente) ha inviato una valutazione su questo utente.
 */
export interface ReviewableUserDto extends UserDto {
  myLastReviewAt: string | null;
  reviewedByOthers: boolean;
}

/** Reparto (Business Unit). */
export interface DepartmentDto {
  id: string;
  name: string;
}

/**
 * Team appartenente a una Business Unit (`departmentId`). Il nome è univoco
 * all'interno della propria Business Unit (non globalmente).
 */
export interface TeamDto {
  id: string;
  name: string;
  /** Business Unit di appartenenza del team. */
  departmentId: string;
}

// ---------------------------------------------------------------------------
// Organigramma (Business Unit → Team → Persone)
// ---------------------------------------------------------------------------

/** Persona (foglia dell'organigramma), con ruolo per la visualizzazione. */
export interface OrgPersonDto {
  id: string;
  displayName: string;
  email: string;
  role: Role;
}

/** Nodo Team dell'organigramma, con le persone assegnate. */
export interface OrgTeamDto {
  id: string;
  name: string;
  members: OrgPersonDto[];
}

/** Nodo Business Unit dell'organigramma, con i suoi team e i membri senza team. */
export interface OrgDepartmentDto {
  id: string;
  name: string;
  teams: OrgTeamDto[];
  /**
   * Persone assegnate alla Business Unit ma a nessun team (`teamId === null`).
   * Restano valutabili solo da manager/admin finché non entrano in un team.
   */
  membersWithoutTeam: OrgPersonDto[];
}

/**
 * Organigramma completo: le Business Unit con i rispettivi team e persone, più
 * le persone non assegnate ad alcuna Business Unit. È la vista dati della
 * pagina admin "Organigramma".
 */
export interface OrgTreeDto {
  departments: OrgDepartmentDto[];
  /** Persone senza Business Unit assegnata (`departmentId === null`). */
  unassigned: OrgPersonDto[];
}

/**
 * Vincoli del peso (importanza) di una skill su una competenza.
 * Il peso è un intero `1..5`; le skill senza peso esplicito valgono
 * {@link DEFAULT_SKILL_WEIGHT} nel calcolo del match.
 */
export const MIN_SKILL_WEIGHT = 1;
export const MAX_SKILL_WEIGHT = 5;
export const DEFAULT_SKILL_WEIGHT = 1;

/**
 * Area e-CF (Dimensione 1 del framework e-Competence): le 5 macro-aree a cui
 * appartiene una competenza. Deriva dal prefisso e-CF del nome (A→Plan,
 * B→Build, C→Run, D→Enable, E→Manage).
 */
export type CompetenceArea = 'Plan' | 'Build' | 'Run' | 'Enable' | 'Manage';

/** Valori ammessi per {@link CompetenceArea}, in ordine e-CF. */
export const COMPETENCE_AREA_VALUES = [
  'Plan',
  'Build',
  'Run',
  'Enable',
  'Manage',
] as const satisfies readonly CompetenceArea[];

/** Competenza con l'elenco delle skill collegate (relazione N:N). */
export interface CompetenceDto {
  id: string;
  name: string;
  description?: string;
  /** Missione/scopo della competenza (testo libero facoltativo). */
  mission?: string;
  /**
   * Area e-CF (Plan/Build/Run/Enable/Manage). Assente sui documenti creati
   * prima dell'introduzione del campo.
   */
  area?: CompetenceArea;
  /** Testo esplicativo del Livello di proficiency e-1 (facoltativo). */
  level1?: string;
  /** Testo esplicativo del Livello di proficiency e-2 (facoltativo). */
  level2?: string;
  /** Testo esplicativo del Livello di proficiency e-3 (facoltativo). */
  level3?: string;
  /** Testo esplicativo del Livello di proficiency e-4 (facoltativo). */
  level4?: string;
  /** Testo esplicativo del Livello di proficiency e-5 (facoltativo). */
  level5?: string;
  /** Reparto di appartenenza (o `null` se non assegnato). */
  departmentId: string | null;
  skillIds: string[];
  /**
   * Peso `1..5` di ciascuna skill su questa competenza, indicizzato per skill id.
   * Le skill assenti dalla mappa valgono {@link DEFAULT_SKILL_WEIGHT}.
   */
  skillWeights: Record<string, number>;
  /**
   * Livello richiesto di ciascuna skill su questa competenza, indicizzato per
   * skill id. Le skill assenti dalla mappa non hanno un Livello_Richiesto.
   */
  skillRequiredLevels: Record<string, Livello>;
}

/** Skill con l'elenco delle competenze collegate (relazione N:N). */
export interface SkillDto {
  id: string;
  name: string;
  /**
   * Descrizione della skill (testo libero facoltativo). Assente sui documenti
   * creati prima dell'introduzione del campo.
   */
  description?: string;
  /**
   * Tipo della skill (`knowledge`/`skill`/`technology`). Assente sui documenti
   * creati prima dell'introduzione del campo.
   */
  type?: SkillType;
  competenceIds: string[];
  /** Se `true`, la skill può essere autovalutata dallo User (Self_Score). */
  selfAssessable: boolean;
  /** Se `true`, la skill può essere valutata tramite quiz AI (AI_Score). */
  aiAssessable: boolean;
}

// ---------------------------------------------------------------------------
// Certificazioni
// ---------------------------------------------------------------------------

/**
 * Certificazione del catalogo generale, esposta dalle API (`_id` serializzato
 * come stringa). Entità condivisa assegnabile a più utenti; opzionalmente
 * collegata a una o più Skill (`skillIds`).
 */
export interface CertificationDto {
  id: string;
  name: string;
  /** Ente che rilascia la certificazione (facoltativo). */
  issuer?: string;
  /** Descrizione testuale (facoltativa). */
  description?: string;
  /** URL della pagina/badge della certificazione (facoltativo). */
  url?: string;
  /** URL dell'immagine/badge (facoltativo). */
  imageUrl?: string;
  /** Id del badge template Credly d'origine (presente se importata da Credly). */
  credlyTemplateId?: string;
  /** Chiave di de‑duplicazione d'origine LinkedIn (presente se importata da LinkedIn). */
  linkedinKey?: string;
  /**
   * `true` se la certificazione è aziendale (appartiene all'azienda, non a una
   * persona): non è assegnabile agli utenti e non ha skill collegate. Assente o
   * `false` = certificazione personale.
   */
  isCompany?: boolean;
  /**
   * Data di conseguimento (ISO-8601) della certificazione aziendale, o `null`.
   * Presente solo per le aziendali (le personali usano le date dell'assegnazione).
   */
  issuedAt?: string | null;
  /** Data di scadenza (ISO-8601) della certificazione aziendale, o `null`. */
  expiresAt?: string | null;
  /** Skill collegate (relazione N:N, facoltativa). */
  skillIds: string[];
  /** Numero di utenti a cui la certificazione è assegnata. */
  assignedCount: number;
}

/**
 * Assegnazione di una certificazione a un utente, esposta dalle API. Arricchita
 * con il nome/issuer della certificazione per le viste che non caricano il
 * catalogo completo.
 */
export interface UserCertificationDto {
  certificationId: string;
  name: string;
  issuer?: string;
  imageUrl?: string;
  /** Skill collegate alla certificazione (relazione N:N). */
  skillIds: string[];
  /** Origine dell'assegnazione. */
  source: 'manual' | 'credly' | 'linkedin';
  /** Data di conseguimento (ISO-8601) o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601) o `null` se non scade. */
  expiresAt: string | null;
  /** URL pubblico del badge per lo specifico utente (Credly), o assente. */
  badgeUrl?: string;
  /**
   * `true` se l'utente ha nascosto l'assegnazione dal proprio profilo. Le
   * assegnazioni nascoste sono escluse da tutte le viste aggregate/insight e dal
   * bonus certificazioni; compaiono solo nella propria vista "Il mio CV", in un
   * elenco separato da cui possono essere rese di nuovo visibili. Assente ⇒
   * assegnazione visibile.
   */
  hidden?: boolean;
}

/**
 * Assegnatario di una certificazione, con le date dell'assegnazione. Restituito
 * dal pannello admin per inizializzare i chip e la tabella delle date.
 */
export interface CertificationAssigneeDto {
  userId: string;
  /** Data di conseguimento (ISO-8601) o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601) o `null` se non scade. */
  expiresAt: string | null;
  /** URL del badge specifico dell'utente (es. la sua pagina Credly), o assente. */
  badgeUrl?: string;
}

/** Body di `POST /api/certifications` (creazione manuale, admin). */
export interface CreateCertificationRequest {
  name: string;
  issuer?: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  /**
   * `true` per creare una certificazione aziendale (non assegnabile, senza
   * skill collegate). Assente o `false` = personale.
   */
  isCompany?: boolean;
  /**
   * Data di conseguimento (ISO-8601) della certificazione aziendale, o `null`.
   * Significativa solo per le aziendali (le personali usano le date
   * dell'assegnazione).
   */
  issuedAt?: string | null;
  /** Data di scadenza (ISO-8601) della certificazione aziendale, o `null`. */
  expiresAt?: string | null;
  skillIds?: string[];
}

/** Body di `PUT /api/certifications/:id` (aggiornamento, admin). */
export interface UpdateCertificationRequest {
  name?: string;
  issuer?: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  /** `true` per una certificazione aziendale (non assegnabile, senza skill). */
  isCompany?: boolean;
  /** Data di conseguimento (ISO-8601) della certificazione aziendale, o `null`. */
  issuedAt?: string | null;
  /** Data di scadenza (ISO-8601) della certificazione aziendale, o `null`. */
  expiresAt?: string | null;
  skillIds?: string[];
}

/** Body di `POST /api/certifications/:id/assignees` (assegnazione a un utente, admin). */
export interface AssignCertificationRequest {
  userId: string;
  /** Data di conseguimento (ISO-8601, facoltativa). */
  issuedAt?: string | null;
  /** Data di scadenza (ISO-8601, facoltativa). */
  expiresAt?: string | null;
  /**
   * URL del badge specifico dell'utente (es. la sua pagina Credly personale).
   * `null` o '' rimuove l'URL salvato; assente lo lascia invariato.
   */
  badgeUrl?: string | null;
}

/** Body di `PUT /api/me/credly` (collega/aggiorna l'URL del profilo Credly). */
export interface SetCredlyProfileRequest {
  /** URL del profilo Credly, oppure `null`/'' per scollegarlo. */
  credlyProfileUrl: string | null;
}

/**
 * Body di `POST /api/certifications/image/presign` (admin): richiede un URL di
 * upload firmato per l'immagine/badge di una certificazione.
 */
export interface PresignCertificationImageRequest {
  /** Content-type del file da caricare (es. `image/png`). */
  contentType: string;
}

/**
 * Risposta della generazione di un presigned URL di upload. Il client esegue un
 * PUT verso `uploadUrl` con header `Content-Type: <contentType>`, poi salva
 * `publicUrl` come `imageUrl` della certificazione.
 */
export interface PresignCertificationImageResponse {
  /** URL PUT firmato verso cui caricare direttamente il file su S3. */
  uploadUrl: string;
  /** Chiave dell'oggetto nel bucket (informativa). */
  key: string;
  /** URL pubblico con cui l'immagine sarà servita una volta caricata. */
  publicUrl: string;
  /** Content-type da usare nel PUT (deve combaciare con la firma). */
  contentType: string;
  /** Scadenza del presigned URL, in secondi. */
  expiresIn: number;
}

/**
 * Esito dell'import Credly del singolo utente. `imported` conta le assegnazioni
 * effettivamente aggiunte (nuove per l'utente); `createdCertifications` conta le
 * certificazioni create nel catalogo generale (badge template non ancora
 * presenti); `skipped` le assegnazioni già presenti (idempotenza); `removed` le
 * assegnazioni Credly rimosse perché non più presenti sul profilo.
 */
export interface ImportCredlyResponse {
  /** Numero di badge letti dal profilo Credly. */
  fetched: number;
  /** Assegnazioni nuove aggiunte all'utente. */
  imported: number;
  /** Certificazioni create nel catalogo generale (de‑dup sul template). */
  createdCertifications: number;
  /** Assegnazioni già presenti per l'utente (saltate). */
  skipped: number;
  /**
   * Assegnazioni di origine Credly rimosse perché non più presenti nel profilo
   * (badge scaduto/revocato/reso privato). Le assegnazioni manuali non contano.
   */
  removed: number;
}

/** Esito dell'import LinkedIn (specchio di ImportCredlyResponse). */
export interface ImportLinkedinResponse {
  fetched: number;
  imported: number;
  createdCertifications: number;
  skipped: number;
  removed: number;
}

/** Avvio del flusso OAuth LinkedIn. */
export interface StartLinkedinLinkResponse {
  authorizationUrl: string;
}

/** Stato del collegamento LinkedIn dell'utente corrente (per il Frontend). */
export interface LinkedinLinkStatusDto {
  linked: boolean;
  consentAt: string | null;
  expiresAt: string | null;
}

/**
 * Riga della vista stats "Certificazioni": una certificazione del catalogo con
 * il numero di persone che la possiedono e le skill collegate (per nome).
 */
export interface CertificationCoverageDto {
  certificationId: string;
  name: string;
  issuer?: string;
  imageUrl?: string;
  /**
   * `true` se aziendale: non è assegnabile e non ha skill. Nella tabella dei
   * livelli la colonna "Persone" mostra un trattino invece del conteggio.
   */
  isCompany?: boolean;
  /**
   * Data di conseguimento (ISO-8601) della certificazione aziendale, o `null`.
   * Presente solo per le aziendali (mostrata nella card della vista Livelli).
   */
  issuedAt?: string | null;
  /** Data di scadenza (ISO-8601) della certificazione aziendale, o `null`. */
  expiresAt?: string | null;
  /** Nomi delle skill collegate. */
  skillNames: string[];
  /** Numero di persone a cui è assegnata. */
  holdersCount: number;
}

/**
 * Payload della vista stats "Certificazioni": copertura aziendale per
 * certificazione. Righe ordinate per `holdersCount` decrescente. In empty state
 * `people = 0` e `certifications = []`.
 */
export interface CertificationLevelsDto {
  /** Numero di persone considerate (l'intero organico). */
  people: number;
  certifications: CertificationCoverageDto[];
}

/**
 * Persona che possiede una certificazione, per la pagina di dettaglio della
 * sezione "Livelli". Arricchita con nome/reparto per la tabella dei possessori.
 */
export interface CertificationHolderDto {
  userId: string;
  displayName: string;
  email: string;
  /** Nome del reparto della persona (o `null` se non assegnato). */
  departmentName: string | null;
  /** Origine dell'assegnazione. */
  source: 'manual' | 'credly' | 'linkedin';
  /** Data di conseguimento (ISO-8601) o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601) o `null` se non scade. */
  expiresAt: string | null;
}

/** Skill collegata a una certificazione, risolta con nome. */
export interface CertificationLinkedSkillDto {
  id: string;
  name: string;
}

/**
 * Dettaglio di una certificazione dal punto di vista dell'utente che la
 * possiede (pagina "Le mie certificazioni" → dettaglio). Contiene i dati del
 * catalogo (nome, ente, descrizione, url, immagine, skill collegate) più i dati
 * della propria assegnazione (origine, date, link al proprio badge). Non
 * espone l'elenco degli altri possessori: è accessibile a qualsiasi utente
 * autenticato che possieda la certificazione.
 */
export interface MyCertificationDetailDto {
  id: string;
  name: string;
  /** Ente che rilascia la certificazione (facoltativo). */
  issuer?: string;
  /** Descrizione testuale (facoltativa). */
  description?: string;
  /** URL della pagina/badge della certificazione (facoltativo). */
  url?: string;
  /** URL dell'immagine/badge (facoltativo). */
  imageUrl?: string;
  /** Skill collegate risolte con nome (relazione N:N). */
  skills: CertificationLinkedSkillDto[];
  /** Origine della propria assegnazione. */
  source: 'manual' | 'credly' | 'linkedin';
  /** Data di conseguimento (ISO-8601) o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601) o `null` se non scade. */
  expiresAt: string | null;
  /**
   * URL del badge da mostrare all'utente. È l'URL specifico della sua
   * assegnazione se presente, altrimenti ricade sull'URL generico della
   * certificazione a catalogo (`url`). Assente solo se nessuno dei due esiste.
   */
  badgeUrl?: string;
}

/**
 * Payload della pagina di dettaglio di una certificazione (sezione "Livelli").
 * Contiene i dati del catalogo, le skill collegate risolte per nome e l'elenco
 * delle persone che l'hanno ottenuta. Accesso protetto da `requireLevelsAccess`.
 */
export interface CertificationDetailDto {
  id: string;
  name: string;
  /** Ente che rilascia la certificazione (facoltativo). */
  issuer?: string;
  /** Descrizione testuale (facoltativa). */
  description?: string;
  /** URL della pagina/badge della certificazione (facoltativo). */
  url?: string;
  /** URL dell'immagine/badge (facoltativo). */
  imageUrl?: string;
  /**
   * `true` se aziendale: non ha skill collegate né possessori. In questo caso
   * `holders` è vuoto, la sezione dei possessori non va mostrata e al suo posto
   * si mostrano le date `issuedAt`/`expiresAt` della certificazione stessa.
   */
  isCompany?: boolean;
  /**
   * Data di conseguimento (ISO-8601) della certificazione aziendale, o `null`.
   * Presente solo per le aziendali.
   */
  issuedAt?: string | null;
  /**
   * Data di scadenza (ISO-8601) della certificazione aziendale, o `null` se non
   * scade. Presente solo per le aziendali.
   */
  expiresAt?: string | null;
  /** Skill collegate risolte con nome (relazione N:N). */
  skills: CertificationLinkedSkillDto[];
  /** Numero di persone che possiedono la certificazione. */
  people: number;
  /** Persone che hanno ottenuto la certificazione, ordinate per nome. */
  holders: CertificationHolderDto[];
}

/**
 * Persona che possiede una valutazione su una skill, per la pagina di dettaglio
 * skill della sezione "Livelli". Estende {@link AssessmentView} (i voti della
 * persona sulla skill: Self, Esperienza, Propensione, AI, Reviews, Score) con
 * i dati identificativi della persona (nome e reparto), così da riusare le
 * stesse colonne della tabella skill.
 */
export interface SkillHolderDto extends AssessmentView {
  /** Identificativo della persona. */
  userId: string;
  /** Nome visualizzato della persona. */
  displayName: string;
  /** Nome del reparto della persona (o `null` se non assegnato). */
  departmentName: string | null;
}

/**
 * Payload della pagina di dettaglio di una skill (sezione "Livelli"). Contiene
 * i dati della skill (nome, descrizione) e l'elenco delle persone che hanno
 * almeno una valutazione su di essa, con i loro voti. Accesso protetto da
 * `requireLevelsAccess`.
 */
export interface SkillDetailDto {
  id: string;
  name: string;
  /** Descrizione testuale della skill (facoltativa). */
  description?: string;
  /** Numero di persone con almeno una valutazione sulla skill. */
  people: number;
  /** Persone con una valutazione sulla skill, ordinate per nome. */
  holders: SkillHolderDto[];
}

/**
 * Persona che copre una competenza, per la pagina di dettaglio competenza della
 * sezione "Livelli". Riporta Copertura e Proficiency della persona sulla
 * competenza, con nome e reparto per la tabella dei possessori.
 */
export interface CompetenceHolderDto {
  userId: string;
  displayName: string;
  /** Nome del reparto della persona (o `null` se non assegnato). */
  departmentName: string | null;
  /** Copertura (`0..100`) della persona sulla competenza. */
  coveragePercent: number;
  /**
   * Proficiency (`0..100`) della persona sulla competenza, o `null` se non
   * calcolabile (nessuna skill della competenza valutata dalla persona).
   */
  proficiencyPercent: number | null;
}

/**
 * Payload della pagina di dettaglio di una competenza (sezione "Livelli").
 * Contiene i dati della competenza (nome, descrizione, mission) e l'elenco
 * delle persone che la coprono almeno in parte, con Copertura e Proficiency.
 * Accesso protetto da `requireLevelsAccess`.
 */
export interface CompetenceDetailDto {
  id: string;
  name: string;
  /** Descrizione testuale della competenza (facoltativa). */
  description?: string;
  /** Missione/scopo della competenza (facoltativa). */
  mission?: string;
  /** Numero di persone che coprono la competenza almeno in parte. */
  people: number;
  /** Persone che coprono la competenza, ordinate per proficiency decrescente. */
  holders: CompetenceHolderDto[];
}

// ---------------------------------------------------------------------------
// Dashboard / KPI
// ---------------------------------------------------------------------------

/** Match di una competenza per la Dashboard. */
export interface CompetencyMatch {
  competenceId: string;
  name: string;
  /**
   * Descrizione estesa della competenza (testo libero facoltativo). Mostrata
   * nella card "hero" della competenza principale in Dashboard.
   */
  description?: string;
  /**
   * Missione/scopo della competenza (testo libero facoltativo), complementare
   * alla `description`. Mostrata nella card "hero" della competenza principale.
   */
  mission?: string;
  /** Nome del reparto di riferimento della competenza (o `null` se non assegnato). */
  departmentName: string | null;
  /**
   * Percentuale di match sui Self_Score, `0..100`. Combina ampiezza e bravura
   * (skill mancanti contano come 0). Mantenuta per retro-compatibilità; la UI
   * mostra separatamente `coveragePercent` e `proficiencyPercent`.
   */
  matchPercent: number;
  /**
   * **Copertura** `0..100`: quanta parte (pesata) delle skill richieste è
   * coperta da skill valutate. Misura l'**ampiezza**, a prescindere dai voti.
   */
  coveragePercent: number;
  /**
   * **Proficiency** `0..100`: bravura media pesata sulle **sole** skill valutate
   * (le mancanti non diluiscono). Misura la **profondità**. `null` quando nessuna
   * skill è valutata.
   */
  proficiencyPercent: number | null;
  /**
   * Punteggio pesato sull'affidabilità del match (smoothing bayesiano sul
   * numero di skill valutate), `0..100`. Determina **solo l'ordinamento** in
   * dashboard (non è più mostrato all'utente).
   */
  rankScore: number;
  /** Numero di skill della competenza con un Self_Score presente. */
  evaluatedSkillCount: number;
  /** Numero totale di skill della competenza. */
  totalSkillCount: number;
}

/**
 * Recensioni ricevute dallo User: sintesi delle valutazioni (submission) che
 * altri valutatori hanno inviato su di lui. Si conta **un valutatore distinto**
 * per la sua ultima submission (nessun doppio conteggio dello stesso rater).
 *
 * In empty state (nessuna recensione ricevuta) tutti i conteggi valgono `0`.
 */
export interface ReviewsReceived {
  /** Numero totale di valutatori distinti che hanno recensito lo User. */
  total: number;
  /** Valutatori distinti con ruolo `reviewer`. */
  reviewerCount: number;
  /** Valutatori distinti con ruolo `manager`. */
  managerCount: number;
}

/**
 * Payload della Dashboard.
 * In empty state: `evaluatedSkillCount = 0`, `competencyMatches = []`,
 * `reviewsReceived` con conteggi a `0` (Req. 9.8).
 */
export interface DashboardDto {
  evaluatedSkillCount: number;
  /** Sintesi delle recensioni (submission) ricevute da reviewer/manager. */
  reviewsReceived: ReviewsReceived;
  /** Ordinato per `rankScore` decrescente (Req. 9.4). */
  competencyMatches: CompetencyMatch[];
}

// ---------------------------------------------------------------------------
// Dashboard "Livelli" — copertura aziendale per competenza
// ---------------------------------------------------------------------------

/**
 * Soglia (sul `rankScore`, scala `0..100`) oltre la quale una persona è
 * considerata "qualificata" su una competenza. Il match di una persona è
 * pesato sull'affidabilità (numero di skill valutate) — vedi `rankScore` in
 * `domain/scoring.ts`.
 */
export const QUALIFIED_RANK_THRESHOLD = 50;

/** Riga di copertura aziendale di una singola competenza. */
export interface CompetenceCoverageDto {
  competenceId: string;
  name: string;
  /** Nome del reparto di riferimento (o `null` se non assegnato). */
  departmentName: string | null;
  /**
   * Numero di persone "qualificate" sulla competenza, cioè con
   * `rankScore >= QUALIFIED_RANK_THRESHOLD`.
   */
  qualifiedCount: number;
  /** Numero totale di persone considerate (l'intero organico). */
  totalPeople: number;
  /**
   * Proficiency media (`0..100`) calcolata **solo sulle persone qualificate**
   * (bravura sulle skill possedute), o `null` se nessuno supera la soglia.
   * Coerente con la metrica Proficiency mostrata nella dashboard per-utente.
   */
  averageProficiency: number | null;
}

/**
 * Payload della dashboard "Livelli": copertura aziendale per competenza. Le
 * righe sono ordinate per `qualifiedCount` decrescente (con `averageProficiency`
 * come tie-breaker). In empty state `people = 0` e `competences = []`.
 */
export interface CatalogLevelsDto {
  /** Numero di persone considerate (l'intero organico). */
  people: number;
  /** Soglia di qualificazione applicata (eco di {@link QUALIFIED_RANK_THRESHOLD}). */
  qualifiedRankThreshold: number;
  competences: CompetenceCoverageDto[];
}

// ---------------------------------------------------------------------------
// Dashboard "Livelli" — copertura aziendale per skill
// ---------------------------------------------------------------------------

/**
 * Riga di copertura aziendale di una singola **skill** (vista "Skill" della
 * dashboard "Livelli").
 *
 * Una persona è considerata "qualificata" sulla skill se ha **almeno uno** dei
 * tre voti presenti (Self, media Reviewer, media Manager). Il voto della
 * persona sulla skill è la **media** dei suoi voti presenti; `weightedAverage`
 * è la media di questi valori **sulle sole persone qualificate**.
 */
export interface SkillCoverageDto {
  skillId: string;
  name: string;
  /**
   * Numero di persone "qualificate" sulla skill, cioè con almeno uno dei tre
   * voti presenti (Self / media Reviewer / media Manager).
   */
  qualifiedCount: number;
  /** Numero totale di persone considerate (l'intero organico). */
  totalPeople: number;
  /**
   * Voto medio pesato (`0..10`) calcolato **solo sulle persone qualificate**:
   * media, tra le persone, del voto medio di ciascuna (media dei suoi voti
   * self/reviewer/manager presenti). `null` se nessuna persona è qualificata.
   */
  weightedAverage: number | null;
}

/**
 * Payload della vista "Skill" della dashboard "Livelli": copertura aziendale per
 * ogni skill del catalogo. Le righe sono ordinate per `qualifiedCount`
 * decrescente (con `weightedAverage` come tie-breaker). In empty state
 * `people = 0` e `skills = []`.
 */
export interface SkillLevelsDto {
  /** Numero di persone considerate (l'intero organico). */
  people: number;
  skills: SkillCoverageDto[];
}

// ---------------------------------------------------------------------------
// Skill suggerite per il reparto (wizard di autovalutazione)
// ---------------------------------------------------------------------------

/**
 * Una skill "suggerita" allo User nel wizard di autovalutazione: una skill
 * appartenente alle competenze del **proprio reparto** (Business Unit) che
 * l'utente non ha ancora valutato, ordinata per popolarità tra i colleghi del
 * reparto.
 *
 * La popolarità (`voteCount`) è il numero complessivo di valutazioni/recensioni
 * che i membri del reparto hanno ricevuto sulla skill, sommando **tutte le
 * fonti di Livello**: autovalutazioni (`livelloSelf`), stime del quiz AI
 * (`aiLevel`) e i voti di `reviewer`/`manager` (una entry per valutatore).
 */
export interface SuggestedSkillDto {
  skillId: string;
  name: string;
  /** Descrizione della skill, se presente nel catalogo. */
  description: string | null;
  /** Numero complessivo di valutazioni/recensioni ricevute nel reparto. */
  voteCount: number;
}

/**
 * Payload delle skill suggerite per il wizard: fino a 20 skill del reparto
 * dell'utente, ordinate per `voteCount` decrescente (tie-breaker: nome). In
 * assenza di reparto o di candidate, `skills = []`.
 */
export interface SuggestedSkillsDto {
  skills: SuggestedSkillDto[];
}

// ---------------------------------------------------------------------------
// Dashboard "Livelli" — matrice skill × Livello (heatmap della vista "Skill")
// ---------------------------------------------------------------------------

/**
 * Aggregato di una singola cella della matrice skill × Livello: le persone che
 * hanno **dichiarato** quel Livello (`livelloSelf`) su quella skill.
 *
 * - `count`: quante persone hanno dichiarato quel Livello sulla skill;
 * - `averageScore`: voto medio (`0..10`) di quelle persone, calcolato sulla
 *   media pesata dei loro voti presenti (self/AI/reviewer/manager). `null`
 *   quando `count = 0` oppure nessuna di quelle persone ha un voto.
 */
export interface SkillLevelCellDto {
  count: number;
  averageScore: number | null;
}

/**
 * Riga della matrice skill × Livello: una skill con, per ciascun Livello della
 * scala (`e-1..e-5`), l'aggregato della relativa cella. Il conteggio è basato
 * sul Livello **auto-dichiarato** (`livelloSelf`), l'unico Livello persistito
 * per persona/skill.
 */
export interface SkillLevelMatrixRowDto {
  skillId: string;
  name: string;
  /** Aggregato per Livello, indicizzato per valore della scala (`e-1..e-5`). */
  cells: Record<Livello, SkillLevelCellDto>;
}

/**
 * Payload della vista "Skill" (matrice heatmap): per ogni skill del catalogo la
 * distribuzione delle persone per Livello dichiarato e il relativo voto medio.
 * In empty state `people = 0` e `skills = []`.
 */
export interface SkillLevelMatrixDto {
  /** Numero di persone considerate (l'intero organico). */
  people: number;
  skills: SkillLevelMatrixRowDto[];
}

// ---------------------------------------------------------------------------
// Dashboard "Livelli" — vista "Team" (persone e loro competenza migliore)
// ---------------------------------------------------------------------------

/**
 * Riga della vista "Team" della dashboard "Livelli": una persona con la sua
 * **competenza migliore**, cioè quella con il `rankScore` più alto (stessa
 * logica di ranking della dashboard per-utente).
 *
 * `topCompetenceId`/`topCompetenceName` e `topProficiencyPercent` sono `null`
 * quando la persona non copre alcuna competenza (nessuna skill valutata): utente
 * senza valutazioni.
 */
export interface TeamMemberDto {
  userId: string;
  displayName: string;
  email: string;
  /** Nome del reparto della persona (o `null` se non assegnato). */
  departmentName: string | null;
  /** Id della competenza migliore (rank più alto) o `null` se assente. */
  topCompetenceId: string | null;
  /** Nome della competenza migliore o `null` se assente. */
  topCompetenceName: string | null;
  /**
   * Proficiency (`0..100`) sulla competenza migliore, o `null` se assente
   * (persona senza skill valutate). Coerente con la Proficiency mostrata nella
   * dashboard per-utente.
   */
  topProficiencyPercent: number | null;
}

/**
 * Payload della vista "Team" della dashboard "Livelli": l'elenco delle persone
 * con la loro competenza migliore e la relativa proficiency. Le righe sono
 * ordinate per `topProficiencyPercent` decrescente (le persone senza dato in
 * coda), poi per nome. In empty state `people = 0` e `members = []`.
 */
export interface TeamLevelsDto {
  /** Numero di persone considerate (l'intero organico). */
  people: number;
  members: TeamMemberDto[];
}

/**
 * Dashboard di un singolo membro del team (drill-down dalla vista "Team"):
 * gli stessi KPI/match della dashboard personale, arricchiti con l'identità
 * della persona (nome/email/reparto) per l'intestazione della pagina. È una
 * vista di **sola lettura**: rappresenta i dati della persona come li vedrebbe
 * lei stessa.
 */
export interface TeamMemberDashboardDto {
  userId: string;
  displayName: string;
  email: string;
  /** Nome del reparto della persona (o `null` se non assegnato). */
  departmentName: string | null;
  /**
   * URL del profilo Credly collegato dalla persona, se presente. Usato dalla
   * dashboard team per offrire (agli admin) l'import delle sue certificazioni.
   */
  credlyProfileUrl?: string;
  /**
   * `true` se la persona ha collegato il proprio account LinkedIn. Usato dalla
   * dashboard team per offrire (agli admin) l'import LinkedIn per suo conto, che
   * richiede un token di consenso già presente (nessun fallback nome-cognome).
   */
  linkedinLinked?: boolean;
  /** KPI e match per competenza della persona (stesso payload della dashboard). */
  dashboard: DashboardDto;
}

// ---------------------------------------------------------------------------
// Auth / sessione
// ---------------------------------------------------------------------------

/** Risposta di `GET /api/me`. */
export type MeResponse = UserDto;

/** Risposta di `GET /api/health`. */
export interface HealthResponse {
  status: 'ok';
}

// ---------------------------------------------------------------------------
// Request/response: utenti e ruoli (admin)
// ---------------------------------------------------------------------------

/** Body di `PATCH /api/users/:id/role`. */
export interface UpdateUserRoleRequest {
  role: Role;
}

/** Body di `PATCH /api/users/:id/department`. */
export interface UpdateUserDepartmentRequest {
  departmentId: string | null;
}

/**
 * Body di `PATCH /api/users/:id/team`. Il `teamId`, se valorizzato, deve
 * riferirsi a un team della Business Unit dell'utente (vincolo imposto lato
 * API); `null` disassocia l'utente da qualsiasi team.
 */
export interface UpdateUserTeamRequest {
  teamId: string | null;
}

/** Singola riga di utente da importare (chiave: `email`). */
export interface ImportUserRow {
  email: string;
  displayName?: string;
  role?: Role;
}

/** Body di `POST /api/users/import`. */
export interface ImportUsersRequest {
  users: ImportUserRow[];
}

/** Esito dell'import di utenti da CSV. */
export interface ImportUsersResponse {
  /** Numero di utenti effettivamente creati. */
  created: number;
  /** Numero di righe saltate perché l'email esiste già. */
  skipped: number;
  /** Email delle righe saltate (già presenti). */
  skippedEmails: string[];
}

// ---------------------------------------------------------------------------
// Request/response: reparti (admin)
// ---------------------------------------------------------------------------

/** Body di `POST /api/departments` e `PUT /api/departments/:id`. */
export interface UpsertDepartmentRequest {
  name: string;
}

// ---------------------------------------------------------------------------
// Request/response: team (admin)
// ---------------------------------------------------------------------------

/** Body di `POST /api/departments/:departmentId/teams` (creazione team). */
export interface CreateTeamRequest {
  name: string;
}

/** Body di `PUT /api/teams/:id` (rinomina team). */
export interface UpdateTeamRequest {
  name: string;
}

// ---------------------------------------------------------------------------
// Request/response: competenze (admin per scrittura)
// ---------------------------------------------------------------------------

/** Body di `POST /api/competences`. */
export interface CreateCompetenceRequest {
  name: string;
  description?: string;
  /** Missione/scopo della competenza (testo libero facoltativo). */
  mission?: string;
  /** Area e-CF (Plan/Build/Run/Enable/Manage), facoltativa. */
  area?: CompetenceArea;
  /** Testo esplicativo del Livello di proficiency e-1 (facoltativo). */
  level1?: string;
  /** Testo esplicativo del Livello di proficiency e-2 (facoltativo). */
  level2?: string;
  /** Testo esplicativo del Livello di proficiency e-3 (facoltativo). */
  level3?: string;
  /** Testo esplicativo del Livello di proficiency e-4 (facoltativo). */
  level4?: string;
  /** Testo esplicativo del Livello di proficiency e-5 (facoltativo). */
  level5?: string;
  departmentId?: string | null;
  skillIds?: string[];
  /** Peso `1..5` per skill (chiave = skill id). Le skill assenti valgono 1. */
  skillWeights?: Record<string, number>;
  /** Livello richiesto per skill (chiave = skill id). Le skill assenti non hanno un Livello_Richiesto. */
  skillRequiredLevels?: Record<string, Livello>;
}

/** Body di `PUT /api/competences/:id`. */
export interface UpdateCompetenceRequest {
  name?: string;
  description?: string;
  /** Missione/scopo della competenza (testo libero facoltativo). */
  mission?: string;
  /** Area e-CF (Plan/Build/Run/Enable/Manage), facoltativa. */
  area?: CompetenceArea;
  /** Testo esplicativo del Livello di proficiency e-1 (facoltativo). */
  level1?: string;
  /** Testo esplicativo del Livello di proficiency e-2 (facoltativo). */
  level2?: string;
  /** Testo esplicativo del Livello di proficiency e-3 (facoltativo). */
  level3?: string;
  /** Testo esplicativo del Livello di proficiency e-4 (facoltativo). */
  level4?: string;
  /** Testo esplicativo del Livello di proficiency e-5 (facoltativo). */
  level5?: string;
  departmentId?: string | null;
  skillIds?: string[];
  /** Peso `1..5` per skill (chiave = skill id). Le skill assenti valgono 1. */
  skillWeights?: Record<string, number>;
  /** Livello richiesto per skill (chiave = skill id). Le skill assenti non hanno un Livello_Richiesto. */
  skillRequiredLevels?: Record<string, Livello>;
}

/** Elemento della risposta di `GET /api/competences/:id/similar`. */
export interface CompetenceSimilarity {
  competenceId: string;
  /** Similarità Jaccard sulle skill condivise, `0..1`. */
  similarity: number;
}

/** Singola riga di competenza da importare (chiave: `name`). */
export interface ImportCompetenceRow {
  name: string;
  description?: string;
  /** Missione/scopo della competenza (testo libero facoltativo). */
  mission?: string;
  /**
   * Area e-CF (Plan/Build/Run/Enable/Manage), facoltativa. Se assente,
   * l'importer la deriva dal prefisso e-CF del nome.
   */
  area?: CompetenceArea;
  /** Testo esplicativo del Livello di proficiency e-1 (facoltativo). */
  level1?: string;
  /** Testo esplicativo del Livello di proficiency e-2 (facoltativo). */
  level2?: string;
  /** Testo esplicativo del Livello di proficiency e-3 (facoltativo). */
  level3?: string;
  /** Testo esplicativo del Livello di proficiency e-4 (facoltativo). */
  level4?: string;
  /** Testo esplicativo del Livello di proficiency e-5 (facoltativo). */
  level5?: string;
}

/**
 * Body di `POST /api/competences/import`.
 *
 * Tutte le competenze importate sono associate al reparto `departmentId`
 * (opzionale: se assente/`null`, vengono create senza reparto).
 */
export interface ImportCompetencesRequest {
  competences: ImportCompetenceRow[];
  departmentId?: string | null;
}

/** Esito dell'import di competenze da CSV. */
export interface ImportCompetencesResponse {
  /** Numero di competenze effettivamente create (nome non ancora presente). */
  created: number;
  /**
   * Numero di competenze già esistenti aggiornate: per i nomi già presenti si
   * aggiornano `description` e `mission` con i valori valorizzati nella riga
   * (i campi vuoti/assenti nella riga non sovrascrivono quelli esistenti).
   */
  updated: number;
  /** Nomi delle competenze aggiornate (già presenti). */
  updatedNames: string[];
  /**
   * Numero di righe saltate: duplicati interni al CSV (stesso nome più volte)
   * oltre la prima occorrenza.
   */
  skipped: number;
  /** Nomi delle righe saltate (duplicati interni al CSV). */
  skippedNames: string[];
}

// ---------------------------------------------------------------------------
// Request/response: skill (admin per scrittura)
// ---------------------------------------------------------------------------

/** Body di `POST /api/skills`. */
export interface CreateSkillRequest {
  name: string;
  description?: string;
  /** Tipo della skill (`knowledge`/`skill`/`technology`). */
  type?: SkillType;
  competenceIds?: string[];
  selfAssessable?: boolean;
  aiAssessable?: boolean;
}

/** Body di `PUT /api/skills/:id`. */
export interface UpdateSkillRequest {
  name?: string;
  description?: string;
  /** Tipo della skill (`knowledge`/`skill`/`technology`). */
  type?: SkillType;
  competenceIds?: string[];
  selfAssessable?: boolean;
  aiAssessable?: boolean;
}

/**
 * Singola riga di skill da importare (chiave: `name`).
 *
 * `level` e `weight` sono opzionali e riguardano l'associazione della skill
 * **alla competenza** indicata nell'import (`competenceId`): quando presenti,
 * popolano rispettivamente `skillRequiredLevels` e `skillWeights` di quella
 * competenza. Senza `competenceId` vengono ignorati (non esiste un'associazione
 * a cui riferirli).
 *
 * `description` è invece un attributo della skill stessa (non dell'associazione):
 * quando valorizzata viene applicata alla skill (creata o esistente),
 * indipendentemente dalla presenza di `competenceId`.
 */
export interface ImportSkillRow {
  name: string;
  /** Descrizione della skill (attributo della skill, non dell'associazione). */
  description?: string;
  /** Tipo della skill (`knowledge`/`skill`/`technology`): attributo della skill. */
  type?: SkillType;
  /** Livello richiesto della skill su questa competenza (`e-1..e-5`). */
  level?: Livello;
  /** Peso `1..5` della skill su questa competenza. */
  weight?: number;
}

/**
 * Body di `POST /api/skills/import`.
 *
 * Tutte le skill importate sono associate alla competenza `competenceId`
 * (opzionale: se assente, le skill vengono create senza associazioni).
 */
export interface ImportSkillsRequest {
  skills: ImportSkillRow[];
  competenceId?: string | null;
}

/** Esito dell'import di skill da CSV. */
export interface ImportSkillsResponse {
  /** Numero di skill effettivamente create. */
  created: number;
  /**
   * Numero di skill esistenti **collegate** alla competenza indicata: skill già
   * a catalogo la cui associazione con questa competenza è stata aggiunta ora
   * (prima non c'era).
   */
  linked: number;
  /** Nomi delle skill collegate (nuova associazione con la competenza). */
  linkedNames: string[];
  /**
   * Numero di skill **modificate**: skill esistenti di cui il CSV ha aggiornato
   * almeno un attributo. Con `competenceId` sono quelle già associate di cui è
   * stato (ri)applicato livello e/o peso e/o la descrizione; senza `competenceId`
   * sono le skill esistenti di cui è stata aggiornata la descrizione.
   */
  updated: number;
  /** Nomi delle skill modificate (livello/peso/descrizione aggiornati). */
  updatedNames: string[];
  /**
   * Numero di skill **cancellate** dalla competenza perché non più presenti nel
   * CSV: viene rimossa l'associazione N:N con questa competenza (la skill resta
   * nel catalogo). Sempre `0` senza `competenceId`.
   */
  removed: number;
  /** Nomi delle skill scollegate dalla competenza per sincronizzazione. */
  removedNames: string[];
  /**
   * Numero di righe saltate: i duplicati interni al CSV (stesso nome ripetuto),
   * le skill già associate senza livello/peso/descrizione da aggiornare, oppure
   * — senza `competenceId` — i nomi già presenti a catalogo per i quali la riga
   * non porta alcuna descrizione (o porta la stessa già presente).
   */
  skipped: number;
  /** Nomi delle righe saltate. */
  skippedNames: string[];
}

/**
 * Esito dell'eliminazione massiva delle skill non associate ad alcuna
 * competenza (skill "orfane": `competenceIds` vuoto).
 */
export interface DeleteUnlinkedSkillsResponse {
  /** Numero di skill orfane effettivamente eliminate. */
  deleted: number;
  /** Nomi delle skill eliminate (ordinati per nome). */
  deletedNames: string[];
}

// ---------------------------------------------------------------------------
// Request/response: valutazioni (Assessment)
// ---------------------------------------------------------------------------

/**
 * Body del `PUT` di autovalutazione dello User.
 * `livello` è obbligatorio: il Livello dichiarato in autovalutazione (una delle
 * Fonti di Livello).
 * `interest` è opzionale (dato puramente informativo): il voto a stelle
 * "volontà di approfondire" (`0..10`), oppure `null` per nessuna preferenza.
 * `yearsOfExperience` è opzionale (dato puramente informativo): la fascia di
 * anni di esperienza dichiarata, oppure `null`/assente se non dichiarata.
 */
export interface SetScoreRequest {
  livello: Livello;
  interest?: number | null;
  yearsOfExperience?: ExperienceRange | null;
}

/** Un voto di skill dentro una submission di valutazione. */
export interface SubmissionScoreDto {
  skillId: string;
  /** Livello votato dal valutatore sulla skill. */
  livello: Livello;
}

/**
 * Body del `POST` di invio di una valutazione (batch datato). Il ruolo con cui
 * i voti sono registrati (`reviewer` o `manager`) è deciso dall'API in base al
 * ruolo del richiedente (l'`admin` agisce da `manager`).
 *
 * Ogni voce di `scores` trasporta il Livello votato dal valutatore sulla skill.
 * Lo storico datato per valutatore resta invariato come meccanismo (una
 * submission per invio, mai sovrascritta); cambia il tipo del voto (Livello).
 */
export interface CreateSubmissionRequest {
  scores: SubmissionScoreDto[];
}

/**
 * Submission datata restituita dall'API (es. precaricamento del form o storico).
 */
export interface AssessmentSubmissionDto {
  id: string;
  targetUserId: string;
  raterId: string;
  raterRole: 'reviewer' | 'manager';
  scores: SubmissionScoreDto[];
  /** ISO-8601. */
  submittedAt: string;
}

// ---------------------------------------------------------------------------
// Request/response: valutazione AI (quiz condotto da AIgor)
// ---------------------------------------------------------------------------

/** Domanda esposta al Frontend: nessuna risposta corretta, nessun flag controllo. */
export interface AiQuizQuestionPublic {
  id: string;
  text: string;
  options: string[];
}

/**
 * Stato della **generazione asincrona** delle domande di un quiz AI, esposto al
 * Frontend per il polling. La generazione può richiedere decine di secondi
 * (oltre il timeout dell'HTTP handler), perciò l'avvio del quiz è disaccoppiato:
 * il client avvia (`/ai/start`), riceve subito uno stato `pending` e fa polling
 * di `GET /ai/sessions/:sessionId` finché non diventa `ready` (domande
 * disponibili) o `failed` (generazione non riuscita, si può riprovare).
 */
export type AiQuizGenerationStatus = 'pending' | 'ready' | 'failed';

/**
 * Risposta all'**avvio** del quiz (`POST /ai/start`). L'avvio è **asincrono**:
 * di norma ritorna subito `status: 'pending'` con il solo `sessionId`, e il
 * client fa polling dello stato. Se le domande erano già disponibili (riuso da
 * un altro utente sulla stessa skill) può ritornare direttamente `status:
 * 'ready'` con la sessione completa già pronta all'uso (nessun polling).
 */
export interface AiQuizStartDto {
  sessionId: string;
  /** Stato della generazione: `pending` (polling necessario) o `ready` (già pronto). */
  status: AiQuizGenerationStatus;
  /** Presente e valorizzato solo quando `status === 'ready'`. */
  session?: AiQuizSessionDto;
}

/**
 * Stato di una sessione di quiz AI, restituito dall'endpoint di **polling**
 * (`GET /ai/sessions/:sessionId`). Finché `status` è `pending`, `session` è
 * assente; a `ready` `session` contiene le domande pubbliche e la finestra
 * temporale; a `failed` la generazione non è riuscita (nessuna `session`).
 */
export interface AiQuizSessionStatusDto {
  sessionId: string;
  status: AiQuizGenerationStatus;
  /** Presente e valorizzato solo quando `status === 'ready'`. */
  session?: AiQuizSessionDto;
}

/**
 * Sessione di quiz AI pronta all'uso: le domande pubbliche e la finestra
 * temporale. È la forma che il Frontend consuma per condurre il quiz una volta
 * che la generazione è `ready`.
 */
export interface AiQuizSessionDto {
  sessionId: string;
  /** ISO-8601: scadenza assoluta del quiz. */
  expiresAt: string;
  /** Durata complessiva in secondi (eco della config). */
  durationSeconds: number;
  questions: AiQuizQuestionPublic[]; // 15 domande, ordine di presentazione
}

/** Singola risposta fornita dallo User. */
export interface AiQuizAnswer {
  questionId: string;
  /** Indice (0-based) dell'opzione scelta. */
  selectedIndex: number;
}

/** Body di invio delle risposte. */
export interface AiQuizSubmitRequest {
  sessionId: string;
  answers: AiQuizAnswer[];
}

// ---------------------------------------------------------------------------
// Errori
// ---------------------------------------------------------------------------

/** Formato uniforme delle risposte di errore. */
export interface ApiError {
  error: {
    code: string;
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Configurazione dello scoring (parametri di calcolo modificabili dall'Admin)
// ---------------------------------------------------------------------------

/**
 * Pesi delle quattro fonti di voto nella media pesata di una skill
 * (`weightedSkillScore`). Valori `>= 0`; almeno uno deve essere `> 0`. Una
 * fonte assente non entra nella media (rinormalizzazione sulle fonti presenti).
 */
export interface SourceWeights {
  self: number;
  ai: number;
  reviewer: number;
  manager: number;
}

/**
 * Numero di fattori di distanza di Livello configurabili: le distanze `1..4`
 * (la distanza `0` vale sempre `1` = 100% ed è fissa, non configurabile).
 * Deriva dalla scala dei Livelli "significativi" `e-1..e-5` (5 elementi →
 * distanza massima `4`). Il Livello minimo `e-0` ("Nessuno") NON estende la
 * scala delle distanze: chi possiede `e-0` è sempre sotto qualunque
 * Livello_Richiesto `e-1..e-5` e viene già escluso dal gate (`f = 0`).
 */
export const LEVEL_DISTANCE_COUNT = LIVELLO_VALUES_EVALUABLE.length - 1;

/**
 * Configurazione globale del motore di scoring, modificabile dall'Admin. È un
 * singleton applicativo: un'unica configurazione valida per tutta l'azienda.
 * Tutti i valori hanno un default (vedi {@link DEFAULT_SCORING_CONFIG}) usato
 * quando la configurazione non è ancora stata salvata.
 */
export interface ScoringConfigDto {
  /**
   * Pesi delle Fonti di Livello (Self, Reviewer, Manager, AI) nella media
   * pesata dei Rango_Livello per skill (Livello_Aggregato).
   */
  sourceWeights: SourceWeights;
  /**
   * Pesi relativi degli assi Livello ed Esperienza nella mappatura verso lo
   * Score_Skill. Valori `>= 0`; almeno uno deve essere `> 0`. Per default il
   * Livello pesa più dell'Esperienza.
   */
  axisWeights: { level: number; experience: number };
  /**
   * Score_Skill minimo (`> 0`, `< 10`) assegnato alla combinazione più bassa
   * (Livello `e-1`, Esperienza `lt-1`).
   */
  minScore: number;
  /**
   * Fattore di aderenza `f ∈ [0, 1]` applicato al voto in base alla distanza
   * tra Livello dichiarato e Livello richiesto. L'indice `i` dell'array è la
   * distanza `d = i + 1` (quindi `levelDistanceFactors[0]` è la distanza `1`,
   * fino a `levelDistanceFactors[LEVEL_DISTANCE_COUNT - 1]` = distanza massima).
   * La distanza `0` (Livello esattamente al richiesto) vale sempre `1` e NON è
   * inclusa qui. Chi è sotto il Livello richiesto ha sempre `f = 0`.
   */
  levelDistanceFactors: number[];
  /**
   * Numero di "skill fantasma" del prior nello smoothing bayesiano del rank
   * (`weightedRankScore`). `>= 0`; più alto penalizza i match basati su poche
   * skill valutate.
   */
  rankSmoothingM: number;
  /** Valore del prior (scala `0..10`) delle skill fantasma dello smoothing. */
  rankSmoothingPrior: number;
  /**
   * Soglia sul `rankScore` (scala `0..100`) oltre la quale una persona è
   * "qualificata" su una competenza (dashboard "Livelli").
   */
  qualifiedRankThreshold: number;
  /** Peso di importanza di default di una skill priva di peso esplicito (`1..5`). */
  defaultSkillWeight: number;
  /**
   * **Bonus certificazioni** in punti percentuali (`>= 0`) applicato allo
   * Score_Skill per **ogni** certificazione valida (non scaduta) posseduta
   * dall'utente e collegata a quella skill. Il contributo è moltiplicativo e
   * cumulativo:
   *
   * ```
   * scoreConBonus = score · (1 + n · certificationBonus / 100)
   * ```
   *
   * dove `n` è il numero di certificazioni valide collegate alla skill. Con
   * `certificationBonus = 0` (default) lo scoring è invariato. Poiché il bonus
   * può spingere lo Score_Skill **oltre 10**, le percentuali di competenza
   * derivate possono a loro volta superare il 100% (contate numericamente, per
   * design). Il bonus si applica solo alle skill con uno Score_Skill già
   * presente (non crea uno score dal nulla).
   */
  certificationBonus: number;
}

/**
 * Configurazione di scoring di default: riproduce esattamente il comportamento
 * storico hardcoded (pesi `self=1, ai=2, reviewer=2, manager=3`; fattori di
 * distanza lineari `1 − 0.225·d` → `0.775, 0.55, 0.325, 0.10`; smoothing
 * `m=3, prior=0`; soglia `50`; peso default `1`; bonus certificazioni `0`). Usata
 * come fallback quando la configurazione non è ancora presa nel database e come
 * valori iniziali del form Admin.
 */
export const DEFAULT_SCORING_CONFIG: ScoringConfigDto = {
  sourceWeights: { self: 1, reviewer: 2, manager: 3, ai: 2 },
  axisWeights: { level: 0.75, experience: 0.25 },
  minScore: 1,
  levelDistanceFactors: [0.775, 0.55, 0.325, 0.1],
  rankSmoothingM: 3,
  rankSmoothingPrior: 0,
  qualifiedRankThreshold: 50,
  defaultSkillWeight: DEFAULT_SKILL_WEIGHT,
  // Nessun bonus certificazioni per default: lo scoring storico è invariato.
  certificationBonus: 0,
};

/** Body di `PUT /api/settings/scoring`: l'intera configurazione da salvare. */
export type UpdateScoringConfigRequest = ScoringConfigDto;

// ---------------------------------------------------------------------------
// Jobs — professioni e-CF 3.0 (catalogo + posizionamento utente)
// ---------------------------------------------------------------------------

/**
 * Professione e-CF 3.0 con l'elenco delle competenze richieste (relazione N:N).
 *
 * Un Job è al catalogo delle competenze come una Competenza è alle skill: ogni
 * competenza richiesta può avere un peso di importanza (`1..5`) e un Livello
 * richiesto (`e-1..e-5`). A differenza delle skill, i Job non hanno assessment:
 * il posizionamento dello User è derivato dai suoi match sulle competenze.
 */
export interface JobDto {
  id: string;
  name: string;
  /** Sintesi/descrizione della professione (colonna `summary` del CSV e-CF). */
  summary?: string;
  /** Missione/scopo della professione. */
  mission?: string;
  /** Deliverable principali. */
  deliverables?: string;
  /** Compiti/attività principali. */
  tasks?: string;
  /** Indicatori chiave di performance (KPI). */
  kpi?: string;
  /** Competenze richieste (relazione N:N). */
  competenceIds: string[];
  /**
   * Peso `1..5` di ciascuna competenza sul Job, indicizzato per competence id.
   * Le competenze assenti dalla mappa valgono {@link DEFAULT_SKILL_WEIGHT}.
   */
  competenceWeights: Record<string, number>;
  /**
   * Livello richiesto di ciascuna competenza sul Job, indicizzato per competence
   * id. Le competenze assenti dalla mappa non hanno un Livello_Richiesto.
   */
  competenceRequiredLevels: Record<string, Livello>;
}

/** Body di `POST /api/jobs`. */
export interface CreateJobRequest {
  name: string;
  summary?: string;
  mission?: string;
  deliverables?: string;
  tasks?: string;
  kpi?: string;
  competenceIds?: string[];
  /** Peso `1..5` per competenza (chiave = competence id). Le assenti valgono 1. */
  competenceWeights?: Record<string, number>;
  /** Livello richiesto per competenza (chiave = competence id). */
  competenceRequiredLevels?: Record<string, Livello>;
}

/** Body di `PUT /api/jobs/:id` (tutti i campi opzionali). */
export interface UpdateJobRequest {
  name?: string;
  summary?: string;
  mission?: string;
  deliverables?: string;
  tasks?: string;
  kpi?: string;
  competenceIds?: string[];
  competenceWeights?: Record<string, number>;
  competenceRequiredLevels?: Record<string, Livello>;
}

/** Singola riga di Job da importare (chiave: `name`). */
export interface ImportJobRow {
  name: string;
  /** Sintesi della professione (alias CSV: `summary`, `descrizione`). */
  summary?: string;
  mission?: string;
  deliverables?: string;
  tasks?: string;
  kpi?: string;
}

/** Body di `POST /api/jobs/import`. */
export interface ImportJobsRequest {
  jobs: ImportJobRow[];
}

/** Esito dell'import di Job da CSV. */
export interface ImportJobsResponse {
  /** Numero di Job effettivamente creati (nome non ancora presente). */
  created: number;
  /**
   * Numero di Job già esistenti aggiornati: per i nomi già presenti si
   * aggiornano i campi testuali valorizzati nella riga (i campi vuoti/assenti
   * non sovrascrivono quelli esistenti).
   */
  updated: number;
  /** Nomi dei Job aggiornati (già presenti). */
  updatedNames: string[];
  /** Numero di righe saltate: duplicati interni al CSV oltre la prima occorrenza. */
  skipped: number;
  /** Nomi delle righe saltate (duplicati interni al CSV). */
  skippedNames: string[];
}

/**
 * Match di uno User su una singola competenza richiesta da un Job, all'interno
 * del posizionamento. Riporta lo score dell'utente su quella competenza
 * (0..100) e il Livello richiesto dal Job (per evidenziare i gap).
 */
export interface JobCompetenceMatch {
  competenceId: string;
  name: string;
  /** Peso della competenza sul Job (`1..5`). */
  weight: number;
  /** Livello richiesto dal Job su questa competenza (o `null` se nessuno). */
  requiredLevel: Livello | null;
  /**
   * Score dello User sulla competenza `0..100` (dalla dashboard: match pesato),
   * oppure `null` se l'utente non ha alcuna skill valutata su quella competenza.
   */
  userScore: number | null;
  /**
   * Voto dello User sulla competenza sulla scala `0..10`, derivato dalla
   * **Proficiency** della competenza (bravura media sulle sole skill valutate),
   * per una resa a chip coerente con gli Score delle skill. `null` se l'utente
   * non ha alcuna skill valutata su quella competenza.
   */
  userScore10: number | null;
  /**
   * Livello posseduto stimato dallo User sulla competenza (`e-0..e-5`), derivato
   * dalla Proficiency. `null` se la competenza non è coperta. Usato per
   * visualizzare la barra dei livelli con il confronto sul Livello richiesto.
   */
  ownedLevel: Livello | null;
  /**
   * `true` se lo User soddisfa il Livello richiesto (o non c'è requisito),
   * `false` se è sotto la soglia richiesta. Sempre `true` quando `requiredLevel`
   * è `null`.
   */
  meetsRequirement: boolean;
  /**
   * Livelli di proficiency `e-1..e-5` che la competenza può effettivamente
   * avere, ossia quelli per cui esiste un testo esplicativo (`level1..level5`)
   * nel catalogo. I livelli assenti da questo elenco vanno mostrati come
   * disattivati nella barra "Analisi Gap".
   */
  availableLevels: Livello[];
}

/**
 * Posizionamento dello User su un Job: quanto il suo profilo di competenze
 * copre e soddisfa le competenze richieste dalla professione.
 */
export interface JobMatch {
  jobId: string;
  name: string;
  summary?: string;
  mission?: string;
  /** Deliverable principali della professione (testo libero facoltativo). */
  deliverables?: string;
  /** Compiti/attività principali della professione (testo libero facoltativo). */
  tasks?: string;
  /** Indicatori chiave di performance (KPI) della professione (testo libero). */
  kpi?: string;
  /**
   * Percentuale di match complessiva `0..100`: media pesata degli score
   * dell'utente sulle competenze richieste (le competenze non coperte contano
   * come 0), con il gate del Livello richiesto applicato.
   */
  matchPercent: number;
  /**
   * Copertura `0..100`: quanta parte (pesata) delle competenze richieste è
   * coperta da competenze su cui l'utente ha almeno una skill valutata.
   */
  coveragePercent: number;
  /**
   * Proficiency `0..100`: bravura media pesata sulle sole competenze coperte
   * (le mancanti non diluiscono). `null` quando nessuna competenza è coperta.
   */
  proficiencyPercent: number | null;
  /** Punteggio di ordinamento (smoothing bayesiano), `0..100`. */
  rankScore: number;
  /** Competenze richieste con almeno una skill valutata dall'utente. */
  coveredCompetenceCount: number;
  /** Numero totale di competenze richieste dal Job. */
  totalCompetenceCount: number;
  /** Dettaglio per competenza richiesta (per evidenziare i gap). */
  competences: JobCompetenceMatch[];
}

/**
 * Payload del posizionamento dello User sui Job.
 * In empty state: `jobMatches = []`.
 */
export interface JobPositioningDto {
  /** Ordinato per `rankScore` decrescente. */
  jobMatches: JobMatch[];
}

// ---------------------------------------------------------------------------
// Jobs — import delle relazioni Job ↔ Competenza (multi-file)
// ---------------------------------------------------------------------------

/**
 * Singola relazione Job → Competenza da importare: il nome della competenza
 * (chiave per risolvere l'`ObjectId`) e il Livello richiesto sul Job.
 */
export interface ImportJobCompetenceRow {
  /** Nome della competenza e-CF (es. "B.1 Sviluppo di Applicazioni"). */
  competenceName: string;
  /** Livello richiesto dal Job su questa competenza (`e-0..e-5`). */
  level?: Livello;
}

/**
 * Relazioni di un singolo Job da importare. Tipicamente ottenuto da un file
 * `job-competences/<Nome Job>.csv` dove il nome del file è il nome del Job.
 */
export interface ImportJobCompetencesEntry {
  /** Nome del Job (chiave: dev'essere già presente o verrà creato). */
  jobName: string;
  /** Righe competenza→livello del Job. */
  competences: ImportJobCompetenceRow[];
}

/** Body di `POST /api/jobs/import-competences`. */
export interface ImportJobCompetencesRequest {
  entries: ImportJobCompetencesEntry[];
}

/** Esito, per singolo Job, dell'import delle relazioni con le competenze. */
export interface ImportJobCompetencesJobResult {
  /** Nome del Job elaborato. */
  jobName: string;
  /** `true` se il Job è stato creato ora (non esisteva). */
  created: boolean;
  /** Numero di competenze effettivamente associate al Job. */
  linkedCompetences: number;
  /** Nomi delle competenze non trovate a catalogo (saltate). */
  missingCompetences: string[];
}

/** Esito complessivo dell'import multi-file delle relazioni Job↔Competenza. */
export interface ImportJobCompetencesResponse {
  /** Numero di Job creati durante l'import. */
  created: number;
  /** Numero di Job aggiornati (già presenti). */
  updated: number;
  /** Numero totale di associazioni Job→Competenza create. */
  linkedCompetences: number;
  /** Nomi delle competenze non trovate a catalogo, uniche, tra tutti i Job. */
  missingCompetences: string[];
  /** Dettaglio per singolo Job. */
  jobs: ImportJobCompetencesJobResult[];
}
// ---------------------------------------------------------------------------
// CV esterni (External CV Management)
// ---------------------------------------------------------------------------

/**
 * Esito della pre-compilazione AI di un profilo CV esterno (condiviso
 * API/Frontend), così che il frontend possa mostrare lo stato della
 * pre-compilazione:
 * - `pending`: pre-compilazione non ancora completata;
 * - `completed`: valutazioni pre-compilate con successo;
 * - `failed`: AI non disponibile o output non conforme (Req. 2.5).
 */
export type ExternalCvAiStatus = 'pending' | 'completed' | 'failed';

// ---------------------------------------------------------------------------
// CV completo in struttura Europass (parsedCv)
// ---------------------------------------------------------------------------

/**
 * Struttura Europass del CV parsato: il contenuto INTERO del CV di un candidato
 * esterno (dati anagrafici, esperienze, istruzione, lingue, competenze,
 * certificazioni, ecc.), organizzato secondo le sezioni del formato Europass per
 * consentire, in futuro, un export nativo in quel formato.
 *
 * Modellazione: tutti i campi sono opzionali (un CV raramente popola ogni
 * sezione, e il parser omette ciò che non trova). Le date sono stringhe libere
 * (i CV usano formati eterogenei: "2019", "03/2020", "Presente"): sono tenute
 * come estratte, senza normalizzazione a ISO. La validazione dell'output del
 * modello e il pruning dei rami vuoti vivono lato API (`domain/europassCv.ts`).
 */

/** Recapito/indirizzo strutturato (Europass "Personal information"). */
export interface EuropassAddress {
  street?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  country?: string;
}

/** Dati anagrafici e di contatto (Europass "Personal information"). */
export interface EuropassPersonalInfo {
  firstName?: string;
  lastName?: string;
  /** Titolo/headline professionale (es. "Senior Backend Engineer"). */
  headline?: string;
  email?: string;
  phone?: string;
  address?: EuropassAddress;
  dateOfBirth?: string;
  nationality?: string;
  gender?: string;
  /** Profili/siti web (LinkedIn, GitHub, portfolio, ecc.). */
  links?: string[];
}

/** Voce di esperienza lavorativa (Europass "Work experience"). */
export interface EuropassWorkExperience {
  jobTitle?: string;
  employer?: string;
  city?: string;
  country?: string;
  /** Inizio, come compare nel CV (es. "2019", "03/2020"). */
  startDate?: string;
  /** Fine, come compare nel CV; testo tipo "Presente" se attuale. */
  endDate?: string;
  /** `true` se il ruolo è tuttora in corso. */
  current?: boolean;
  /** Descrizione di mansioni/responsabilità/risultati. */
  description?: string;
  /** Settore/attività dell'azienda o del datore di lavoro. */
  sector?: string;
}

/** Voce di istruzione o formazione (Europass "Education and training"). */
export interface EuropassEducation {
  /** Qualifica/titolo conseguito. */
  qualification?: string;
  /** Istituto/organizzazione erogante. */
  institution?: string;
  city?: string;
  country?: string;
  startDate?: string;
  endDate?: string;
  current?: boolean;
  /** Materie principali, competenze acquisite o descrizione libera. */
  description?: string;
  /** Livello EQF/ISCED se esplicitato nel CV. */
  level?: string;
}

/**
 * Competenza linguistica (Europass "Language skills"). I livelli sono liberi
 * (es. "C1", "madrelingua", "buono"): il CV non usa sempre il quadro CEFR.
 */
export interface EuropassLanguageSkill {
  language?: string;
  /** `true` se lingua madre. */
  mother?: boolean;
  listening?: string;
  reading?: string;
  spokenInteraction?: string;
  spokenProduction?: string;
  writing?: string;
  /** Livello complessivo, quando il CV non distingue le abilità. */
  overall?: string;
}

/** Certificazione/attestato citato nel CV (Europass "Certifications"). */
export interface EuropassCertification {
  name?: string;
  issuer?: string;
  /** Data di conseguimento come compare nel CV. */
  date?: string;
  /** Identificativo/URL di verifica, se presente. */
  reference?: string;
}

/** Progetto rilevante citato nel CV (sezione "Projects", opzionale). */
export interface EuropassProject {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  /** Tecnologie/strumenti impiegati. */
  technologies?: string[];
}

/** Riconoscimento/premio (sezione "Honours and awards", opzionale). */
export interface EuropassAward {
  title?: string;
  issuer?: string;
  date?: string;
  description?: string;
}

/** Pubblicazione (sezione "Publications", opzionale). */
export interface EuropassPublication {
  title?: string;
  publisher?: string;
  date?: string;
  reference?: string;
}

/** Competenze non linguistiche (Europass "Digital/other skills"). */
export interface EuropassSkills {
  /** Competenze comunicative (testo libero). */
  communication?: string;
  /** Competenze organizzative/gestionali (testo libero). */
  organisational?: string;
  /** Competenze professionali/tecniche (testo libero). */
  jobRelated?: string;
  /** Competenze digitali (testo libero o elenco). */
  digital?: string;
  /** Altre competenze (hobby, interessi rilevanti, ecc.). */
  other?: string;
}

/**
 * CV completo in struttura Europass. È il "mega oggetto JSON" persistito sul
 * profilo esterno (`externalCvProfiles.parsedCv`) e serializzato tal quale nel
 * DTO di dettaglio.
 */
export interface EuropassCv {
  personalInfo?: EuropassPersonalInfo;
  /** Sintesi/obiettivo professionale (Europass "About me"). */
  summary?: string;
  workExperience?: EuropassWorkExperience[];
  education?: EuropassEducation[];
  languages?: EuropassLanguageSkill[];
  skills?: EuropassSkills;
  certifications?: EuropassCertification[];
  projects?: EuropassProject[];
  awards?: EuropassAward[];
  publications?: EuropassPublication[];
  /** Codice lingua rilevata del CV (ISO 639-1, es. "it", "en"), se deducibile. */
  detectedLanguage?: string;
}

// ---------------------------------------------------------------------------
// Import del CV da LinkedIn (Member Snapshot API → Europass)
// ---------------------------------------------------------------------------

/**
 * Domini del Member Snapshot API di LinkedIn interrogati durante l'import del
 * CV, uno per Cv_Section Europass di destinazione. I valori corrispondono al
 * parametro `domain` dell'API esterna e sono condivisi tra service e parser,
 * così che la nomenclatura resti definita in un unico punto.
 */
export const LINKEDIN_DOMAINS = {
  PROFILE: 'PROFILE',
  POSITIONS: 'POSITIONS',
  EDUCATION: 'EDUCATION',
  SKILLS: 'SKILLS',
  LANGUAGES: 'LANGUAGES',
  CERTIFICATIONS: 'CERTIFICATIONS',
} as const;

/** Dominio LinkedIn interrogato durante l'import: uno dei valori ammessi. */
export type LinkedinDomain = (typeof LINKEDIN_DOMAINS)[keyof typeof LINKEDIN_DOMAINS];

/**
 * Riepilogo per Cv_Section dell'esito dell'import: quali sezioni sono state
 * valorizzate dall'import (erano vuote) e quali sono state conservate (erano
 * già valorizzate). Non espone l'Access_Token né altri segreti del canale
 * LinkedIn: contiene solo nomi di sezione e conteggi.
 */
export interface LinkedinCvImportSummary {
  /** Nomi delle Cv_Section valorizzate dall'import (erano vuote). */
  populatedSections: (keyof EuropassCv)[];
  /** Nomi delle Cv_Section conservate (erano già valorizzate). */
  preservedSections: (keyof EuropassCv)[];
  /** Numero di sezioni popolate (= `populatedSections.length`). */
  populatedCount: number;
  /** Numero di sezioni conservate (= `preservedSections.length`). */
  preservedCount: number;
}

/**
 * Esito sincrono dell'import del CV da LinkedIn: l'Import_Summary e il
 * Parsed_Cv aggiornato dopo il merge non distruttivo e il pruning dei rami
 * vuoti. Non contiene alcun segreto del canale LinkedIn.
 */
export interface LinkedinCvImportResponse {
  /** Riepilogo per sezione: popolate dall'import vs conservate. */
  summary: LinkedinCvImportSummary;
  /** Parsed_Cv aggiornato dopo il merge non distruttivo e il pruning. */
  parsedCv: EuropassCv;
}

/**
 * Segnalazione sintetica dell'interesse di un candidato, derivata da un
 * punteggio composito (vedi `computeCandidateSignal`). Solo due livelli positivi
 * sono segnalati; i profili sotto la soglia o non ancora valutabili non ricevono
 * alcuna segnalazione (`null`), per evitare di marcare come "deboli" i candidati.
 *
 * - `promising`  → profilo promettente (punteggio composito alto);
 * - `toEvaluate` → profilo da valutare (punteggio composito medio).
 */
export type CandidateSignal = 'promising' | 'toEvaluate';

/**
 * Vista di elenco di un profilo CV esterno (Req. 3.1): dati sintetici, senza
 * valutazioni. `createdAt` è serializzata come stringa ISO-8601.
 */
export interface ExternalCvProfileSummary {
  id: string;
  firstName: string;
  lastName: string;
  /** Data di creazione del profilo (ISO-8601). */
  createdAt: string;
  /** Esito della pre-compilazione AI (Req. 2.5). */
  aiStatus: ExternalCvAiStatus;
  /**
   * Nome della competenza principale (Competency_Match con `rankScore` più
   * alto) derivata in memoria dalle Cv_Evaluation, o `null` se il profilo non
   * copre alcuna competenza (Req. 3.5, 3.7, 3.8). Stesso significato del campo
   * omonimo di {@link TeamMemberDto} della vista Team.
   */
  topCompetenceName: string | null;
  /**
   * Proficiency (`0..100`) sulla competenza principale, o `null` se assente
   * (nessuna Cv_Evaluation valorizzata — Req. 3.7, 3.8). Stesso significato del
   * campo omonimo di {@link TeamMemberDto} della vista Team.
   */
  topProficiencyPercent: number | null;
  /**
   * Nome della professione principale: il Job con `rankScore` più alto tra
   * quelli con un match reale (`matchPercent > 0`), o `null` se il profilo non
   * ha alcun match reale su alcuna professione. Il filtro `matchPercent > 0`
   * replica quello del dettaglio del profilo, così l'elenco non mostra mai una
   * professione che il dettaglio nasconde. Derivato in memoria dalle
   * Cv_Evaluation con lo stesso motore del posizionamento ({@link JobMatch}).
   */
  topJobName: string | null;
  /**
   * Proficiency (`0..100`) sulla professione principale, o `null` se assente
   * (nessuna competenza richiesta coperta). Corrisponde a
   * `JobMatch.proficiencyPercent` del Job selezionato come principale.
   */
  topJobProficiencyPercent: number | null;
  /**
   * Segnalazione sintetica dell'interesse del candidato (punteggio composito).
   * `null` quando il profilo non raggiunge la soglia minima o non è ancora
   * valutabile: in tal caso nessun badge è mostrato in elenco.
   */
  signal: CandidateSignal | null;
}

/**
 * Singola valutazione esposta nel dettaglio di un profilo CV esterno: la skill
 * riconosciuta (id + nome risolto dal catalogo) con Livello ed Experience_Range
 * (Req. 3.2). `level` è sempre nella scala significativa `e-1..e-5`.
 */
export interface ExternalCvEvaluationDto {
  skillId: string;
  skillName: string;
  level: Livello;
  experienceRange: ExperienceRange;
}

/**
 * Singola certificazione esposta nel dettaglio di un profilo CV esterno,
 * importata automaticamente da Credly per nome+cognome. I dati identificativi
 * (nome/ente/immagine/URL) sono risolti dal catalogo condiviso `certifications`;
 * le date provengono dal badge Credly.
 */
export interface ExternalCvCertificationDto {
  certificationId: string;
  name: string;
  issuer?: string;
  imageUrl?: string;
  url?: string;
  /** URL pubblico del badge conseguito (Credly), o `undefined`. */
  badgeUrl?: string;
  /** Skill collegate alla certificazione, risolte con nome dal catalogo. */
  skills: CertificationLinkedSkillDto[];
  /** Data di conseguimento (ISO-8601), o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601), o `null` se non scade. */
  expiresAt: string | null;
}

/**
 * Vista di dettaglio di un profilo CV esterno (Req. 3.2): estende la vista di
 * elenco con l'elenco delle Cv_Evaluation (skill risolte dal catalogo) e le
 * certificazioni importate da Credly.
 */
export interface ExternalCvProfileDetail extends ExternalCvProfileSummary {
  evaluations: ExternalCvEvaluationDto[];
  /** Certificazioni importate da Credly, associate al profilo. */
  certifications: ExternalCvCertificationDto[];
  /**
   * CV completo estratto dal Cv_Pdf in struttura Europass (il "mega oggetto
   * JSON"), o `null` se l'estrazione non è ancora avvenuta o è fallita. Pensato
   * per un futuro export nativo in formato Europass.
   */
  parsedCv: EuropassCv | null;
  /** Esito dell'estrazione Europass (indipendente da `aiStatus`). */
  parsedCvStatus: ExternalCvAiStatus;
  /**
   * Public_Token condivisibile associato al profilo, o `null` se non è ancora
   * stato generato. Presente sui profili creati dal flusso pubblico e su quelli
   * per cui il token è stato generato/rigenerato dal dettaglio candidato.
   * Il client costruisce l'URL pubblico come `/public/submission/:publicToken`.
   */
  publicToken: string | null;
}

/**
 * Dettaglio arricchito di un profilo CV esterno, che rispecchia la
 * Team_Member_Dashboard aggregando le stesse forme (KPI e match per competenza
 * dalla {@link DashboardDto}, tabella skill valutate dalle {@link AssessmentView}
 * e posizionamento sulle professioni dalla {@link JobPositioningDto}) insieme
 * alla sintesi del profilo ({@link ExternalCvProfileSummary}).
 *
 * A differenza della dashboard di un utente reale, tutti questi dati sono
 * derivati esclusivamente in memoria dalle Cv_Evaluation del profilo, senza
 * alcun dato persistito sugli utenti reali (isolamento, Req. 8).
 */
export interface ExternalCvDashboardDto {
  profile: ExternalCvProfileSummary;
  dashboard: DashboardDto;
  assessments: AssessmentView[];
  jobPositioning: JobPositioningDto;
  /** Certificazioni importate da Credly, associate al profilo. */
  certifications: ExternalCvCertificationDto[];
}

// ---------------------------------------------------------------------------
// Live review skill (review dal vivo di un candidato esterno)
// ---------------------------------------------------------------------------

/**
 * Body del `POST` di invio di una live review (batch datato) su un candidato
 * esterno. Riusa {@link SubmissionScoreDto} per i voti di Livello; la regola di
 * dominio "array non vuoto" e "Livello in `e-1..e-5`" è applicata dal servizio.
 */
export interface CreateLiveReviewRequest {
  scores: SubmissionScoreDto[];
  /** Unica nota libera dell'intera review (facoltativa). */
  note?: string | null;
}

/**
 * Live review datata restituita dall'API (precaricamento o storico). Lo storico
 * è append-only: ogni invio produce una nuova submission con un proprio
 * `submittedAt`.
 */
export interface LiveReviewSubmissionDto {
  id: string;
  externalCvProfileId: string;
  raterId: string;
  scores: SubmissionScoreDto[];
  note: string | null;
  /** ISO-8601. */
  submittedAt: string;
}

// ---------------------------------------------------------------------------
// CV pubblico (flusso anonimo upload → recap → dashboard read-only)
// ---------------------------------------------------------------------------

/** URL di default dell'informativa privacy per il flusso CV pubblico. */
export const DEFAULT_PUBLIC_CV_PRIVACY_POLICY_URL =
  'https://appfactory.it/assets/pdf/INFORMATIVA%20SUL%20TRATTAMENTO%20DEI%20DATI%20PERSONALI.pdf';

/** Impostazioni del flusso CV pubblico (configurabili da admin). */
export interface PublicCvSettingsDto {
  /** URL dell'informativa privacy mostrata e sottoscritta nel form pubblico. */
  privacyPolicyUrl: string;
  /**
   * URL pubblico del logo mostrato centrato in cima al form pubblico. Stringa
   * vuota quando nessun logo è configurato.
   */
  logoUrl: string;
  /**
   * URL pubblico dell'immagine di sfondo della pagina pubblica. Stringa vuota
   * quando nessuno sfondo è configurato.
   */
  backgroundUrl: string;
  /**
   * Colore primario di accent della pagina pubblica in formato esadecimale
   * (es. `#C63968`). Stringa vuota per usare il colore di default del tema.
   */
  primaryColor: string;
}

/** Body di PUT /api/settings/public-cv. */
export interface UpdatePublicCvSettingsRequest {
  privacyPolicyUrl: string;
  /** URL pubblico del logo (stringa vuota per rimuoverlo). */
  logoUrl: string;
  /** URL pubblico dell'immagine di sfondo (stringa vuota per rimuoverla). */
  backgroundUrl: string;
  /** Colore primario esadecimale (stringa vuota per usare il default). */
  primaryColor: string;
}

/**
 * Sintesi pubblica di un profilo CV esterno: identica a
 * {@link ExternalCvProfileSummary} ma **senza** l'identificativo interno `id`,
 * che non deve mai comparire nelle risposte del flusso pubblico (Req. 2.2).
 * `createdAt` è serializzata come stringa ISO-8601.
 */
/**
 * Risposta della generazione di un presigned URL di upload per il Cv_Pdf
 * pubblico. Il client esegue un `PUT` diretto verso `uploadUrl` con header
 * `Content-Type: <contentType>` (deve combaciare con la firma), poi usa `key`
 * come `cvPdfKey` nella creazione del profilo. Rispecchia il `PresignedUpload`
 * restituito da `uploadService` (Req. 1.1).
 */
export interface PresignedUpload {
  /** URL PUT firmato verso cui il client carica il file (scadenza breve). */
  uploadUrl: string;
  /** Chiave dell'oggetto nel bucket (usata come `cvPdfKey`). */
  key: string;
  /** URL pubblico con cui l'oggetto sarà servito una volta caricato. */
  publicUrl: string;
  /** Content-type che il client DEVE usare nel PUT (deve combaciare con la firma). */
  contentType: string;
  /** Scadenza del presigned URL in secondi. */
  expiresIn: number;
}

export interface PublicExternalCvProfileSummary {
  firstName: string;
  lastName: string;
  /** Data di creazione del profilo (ISO-8601). */
  createdAt: string;
  /** Esito della pre-compilazione AI (Req. 2.5). */
  aiStatus: ExternalCvAiStatus;
  /**
   * Nome della competenza principale (Competency_Match con `rankScore` più
   * alto), o `null` se il profilo non copre alcuna competenza.
   */
  topCompetenceName: string | null;
  /**
   * Proficiency (`0..100`) sulla competenza principale, o `null` se assente
   * (nessuna Cv_Evaluation valorizzata).
   */
  topProficiencyPercent: number | null;
}

/**
 * Esito della creazione pubblica di un profilo: espone **solo** il Public_Token
 * opaco e lo stato AI iniziale, mai l'identificativo interno del profilo.
 */
export interface PublicCvCreateResult {
  /** Public_Token opaco e non indovinabile (base64url, URL-safe). */
  token: string;
  /** Stato AI alla creazione (tipicamente `pending`). */
  aiStatus: ExternalCvAiStatus;
}

/**
 * Stato AI restituito dall'endpoint di polling della schermata di recap
 * (Req. 3.1): il candidato interroga periodicamente questo valore finché la
 * pre-compilazione non è `completed` o `failed`.
 */
export interface PublicCvStatusDto {
  aiStatus: ExternalCvAiStatus;
}

/**
 * Dashboard pubblica read-only: rispecchia {@link ExternalCvDashboardDto} ma
 * con la sintesi del profilo priva dell'identificativo interno
 * ({@link PublicExternalCvProfileSummary}). Tutti gli altri aggregati (KPI,
 * match per competenza, skill valutate, posizionamento, certificazioni) sono
 * riusati invariati dall'aggregazione interna (Req. 4.1).
 */
export interface PublicExternalCvDashboardDto {
  profile: PublicExternalCvProfileSummary;
  dashboard: DashboardDto;
  assessments: AssessmentView[];
  jobPositioning: JobPositioningDto;
  /** Certificazioni importate da Credly, associate al profilo. */
  certifications: ExternalCvCertificationDto[];
  /**
   * CV completo estratto dal Cv_Pdf in struttura Europass (il "mega oggetto
   * JSON"), o `null` se l'estrazione non è ancora avvenuta o è fallita. Esposto
   * anche nel flusso pubblico per rendere le sezioni del CV (dati personali,
   * esperienza, istruzione, lingue, progetti, certificazioni del CV, ecc.).
   */
  parsedCv: EuropassCv | null;
  /** Esito dell'estrazione Europass (indipendente da `aiStatus`). */
  parsedCvStatus: ExternalCvAiStatus;
}

/**
 * Singola valutazione skill del profilo CV esterno esposta al flusso pubblico
 * (autovalutazione del candidato che sovrascrive il livello pre-compilato
 * dall'AI). `experienceRange` è opzionale (`null` = non indicata).
 */
export interface PublicCvEvaluationDto {
  skillId: string;
  level: Livello;
  experienceRange: ExperienceRange | null;
}

/**
 * Body di `PUT /api/public/public-cv/profiles/:token/evaluations/:skillId`.
 *
 * `level` a `e-0` ("Nessuno") rimuove la valutazione della skill dal profilo;
 * qualsiasi altro Livello la crea/sovrascrive. `experienceRange` è opzionale.
 */
export interface UpdatePublicCvEvaluationRequest {
  level: Livello;
  experienceRange?: ExperienceRange | null;
}

/* ===========================================================================
 * API keys — chiavi d'accesso alle API pubbliche (header `x-api-key`).
 * Gestione admin (lista/crea/elimina) e contratti degli endpoint pubblici.
 * =========================================================================== */

/**
 * API key esposta all'admin nella lista. Non contiene mai il valore in chiaro
 * (irrecuperabile dopo la creazione): solo il prefisso leggibile e i metadati.
 */
export interface ApiKeyDto {
  id: string;
  /** Etichetta descrittiva scelta dall'admin. */
  label: string;
  /** Primi caratteri della chiave (es. `afsk_a1b2c3`), per riconoscerla. */
  keyPrefix: string;
  /** Data di creazione (ISO-8601). */
  createdAt: string;
  /** Ultimo utilizzo (ISO-8601), o `null` se mai usata. */
  lastUsedAt: string | null;
}

/** Body di `POST /api/api-keys` (creazione, admin). */
export interface CreateApiKeyRequest {
  /** Etichetta descrittiva (obbligatoria). */
  label: string;
}

/**
 * Risposta di `POST /api/api-keys`. Contiene la chiave in chiaro (`key`),
 * mostrata **una sola volta**: non è più recuperabile in seguito.
 */
export interface CreateApiKeyResponse {
  /** Metadati della chiave creata. */
  apiKey: ApiKeyDto;
  /** Valore della chiave in chiaro, da copiare subito (mostrato una volta). */
  key: string;
}

/**
 * Certificazione aziendale esposta dall'endpoint pubblico
 * `GET /api/public/certifications/company` (autenticazione via `x-api-key`).
 * Le certificazioni aziendali appartengono all'azienda (non a una persona) e
 * portano le proprie date di conseguimento/scadenza.
 */
export interface PublicCompanyCertificationDto {
  id: string;
  name: string;
  /** Ente che rilascia la certificazione (facoltativo). */
  issuer?: string;
  /** Descrizione testuale (facoltativa). */
  description?: string;
  /** URL della pagina/badge della certificazione (facoltativo). */
  url?: string;
  /** URL dell'immagine/badge (facoltativo). */
  imageUrl?: string;
  /** Data di conseguimento (ISO-8601), o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601), o `null` se non scade. */
  expiresAt: string | null;
}

/**
 * Certificazione personale di un dipendente, esposta dall'endpoint pubblico
 * `GET /api/public/users/certifications?email=<email>` (via `x-api-key`).
 */
export interface PublicUserCertificationDto {
  /** Id della certificazione a catalogo. */
  certificationId: string;
  name: string;
  /** Ente che rilascia la certificazione (facoltativo). */
  issuer?: string;
  /** URL dell'immagine/badge (facoltativo). */
  imageUrl?: string;
  /** Origine dell'assegnazione. */
  source: 'manual' | 'credly' | 'linkedin';
  /** Data di conseguimento (ISO-8601), o `null`. */
  issuedAt: string | null;
  /** Data di scadenza (ISO-8601), o `null` se non scade. */
  expiresAt: string | null;
  /** URL pubblico del badge per lo specifico utente (Credly), o assente. */
  badgeUrl?: string;
}

/**
 * Risposta dell'endpoint pubblico delle certificazioni personali: i dati
 * essenziali del dipendente più l'elenco delle sue certificazioni.
 */
export interface PublicUserCertificationsResponse {
  /** Email del dipendente (normalizzata). */
  email: string;
  /** Nome visualizzato del dipendente. */
  displayName: string;
  /** Certificazioni possedute, ordinate per nome. */
  certifications: PublicUserCertificationDto[];
}

// ---------------------------------------------------------------------------
// Explore — ricerche salvate di talenti (persone + candidati) per skill/livello
// ---------------------------------------------------------------------------

/**
 * Singola skill cercata da un'esplorazione, con il Livello minimo richiesto.
 * Forma esposta dalle API (`skillId` serializzato come stringa hex).
 */
export interface ExplorationSkillDto {
  /** Id della skill del catalogo. */
  skillId: string;
  /** Livello minimo richiesto per la skill (scala Livello). */
  requiredLevel: Livello;
}

/**
 * Esplorazione esposta dalle API: ricerca salvata di talenti per un insieme di
 * skill, ciascuna con un Livello minimo richiesto. Creabile da qualsiasi utente
 * autenticato (`createdBy` è l'autore).
 */
export interface ExplorationDto {
  id: string;
  name: string;
  /** Id dell'utente che ha creato l'esplorazione. */
  createdBy: string;
  /** Skill cercate, ciascuna con il proprio Livello minimo richiesto. */
  skills: ExplorationSkillDto[];
  /** Data di creazione (ISO-8601). */
  createdAt: string;
  /** Data dell'ultimo aggiornamento (ISO-8601). */
  updatedAt: string;
}

/**
 * Riga di elenco delle esplorazioni (vista lista). Omette l'elenco completo
 * delle skill, riportandone il solo conteggio per la tabella.
 */
export interface ExplorationSummaryDto {
  id: string;
  name: string;
  createdBy: string;
  /** Numero di skill cercate dall'esplorazione. */
  skillCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Body di `POST /api/explorations` (creazione, qualsiasi autenticato). */
export interface CreateExplorationRequest {
  name: string;
  skills: ExplorationSkillDto[];
}

/** Body di `PUT /api/explorations/:id` (aggiornamento, solo l'autore). */
export interface UpdateExplorationRequest {
  name?: string;
  skills?: ExplorationSkillDto[];
}

/** Tipo di soggetto restituito da un match di esplorazione. */
export type ExploreMatchKind = 'person' | 'candidate';

/**
 * Dettaglio per-skill del matching di un soggetto: il Livello posseduto e se
 * soddisfa il Livello richiesto dall'esplorazione. Serve alla UI per spiegare
 * perché un soggetto compare in cima (o in coda) ai risultati.
 */
export interface ExploreSkillMatchDto {
  /** Id della skill cercata. */
  skillId: string;
  /** Nome della skill cercata. */
  skillName: string;
  /** Livello richiesto dall'esplorazione per questa skill. */
  requiredLevel: Livello;
  /**
   * Livello posseduto dal soggetto (aggregato per le persone, dichiarato per i
   * candidati), oppure `null` se il soggetto non ha alcuna valutazione sulla
   * skill.
   */
  ownedLevel: Livello | null;
  /**
   * `true` se il Livello posseduto soddisfa il Livello richiesto
   * (`ownedLevel >= requiredLevel`). `false` se insufficiente o assente.
   */
  meetsRequirement: boolean;
}

/**
 * Soggetto (persona o candidato) che compare tra i risultati di
 * un'esplorazione, con le metriche di matching aggregate e il dettaglio
 * per-skill. Ordinato lato API per `rankScore` decrescente.
 */
export interface ExploreMatchDto {
  /** Tipo di soggetto: utente reale (`person`) o candidato esterno (`candidate`). */
  kind: ExploreMatchKind;
  /** Id del soggetto (`users._id` o `externalCvProfiles._id`). */
  id: string;
  /** Nome visualizzato del soggetto. */
  displayName: string;
  /**
   * Percentuale di match pesata sulle skill cercate (`0..100`): tiene conto
   * sia della copertura sia dell'aderenza al Livello richiesto.
   */
  matchPercent: number;
  /**
   * Copertura (`0..100`): quante delle skill cercate il soggetto possiede (a
   * prescindere dall'aver raggiunto il Livello richiesto).
   */
  coveragePercent: number;
  /** Numero di skill cercate che il soggetto soddisfa pienamente (Livello ok). */
  metCount: number;
  /** Numero totale di skill cercate dall'esplorazione. */
  totalSkillCount: number;
  /**
   * Propensione media (`0..100`) del soggetto sulle skill cercate possedute
   * (volontà di approfondire), oppure `null` se non disponibile. È sempre
   * `null` per i candidati esterni, che non esprimono propensione.
   */
  propensionPercent: number | null;
  /** Dettaglio per-skill (una entry per skill cercata). */
  skills: ExploreSkillMatchDto[];
}

/**
 * Payload dei risultati di un'esplorazione: i dati dell'esplorazione più i
 * soggetti (persone + candidati) ordinati per qualità del match decrescente.
 */
export interface ExploreResultsDto {
  exploration: ExplorationDto;
  /**
   * Soggetti con almeno una skill cercata coperta, ordinati per qualità del
   * match decrescente. In empty state (esplorazione senza skill o nessun
   * soggetto con dati) l'elenco è vuoto.
   */
  matches: ExploreMatchDto[];
}
